import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Avatar, Button, MenuItem, Menu, Box, Modal, Select, FormControl, Chip, OutlinedInput, ClickAwayListener, Typography, IconButton, Popover } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import TextareaAutosize from 'react-textarea-autosize';
import TextEditor from "../../common/TextEditorRebranded";
import { IoClose } from 'react-icons/io5';
import { TbPhoto, TbVideo, TbMicrophone, TbFile, TbCalendar, TbClock, TbLink, TbFileText, TbTag, TbPlus, TbChevronUp, TbPencil, TbX } from "react-icons/tb";
import { FaFilePdf } from 'react-icons/fa6';
import { RiFullscreenFill } from 'react-icons/ri';
import { ImEmbed2 } from "react-icons/im";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import ZoomImage from '../../../media/site_media/platform-icons/zoom.png';
import MicrosoftTeams from '../../../media/site_media/platform-icons/microsoft-teams.png';
import GoogleMeetImage from '../../../media/site_media/platform-icons/google-meet.png';
import GoogleSheetsImage from '../../../media/site_media/platform-icons/google-sheets.png';
import GoogleDocsImage from '../../../media/site_media/platform-icons/google-docs.png';
import VideoJS from '../../common/VideoJS';
import ReactAudioPlayer from "react-audio-player";
import PDFSwiper from "../../Content/ContentSubComponents/PDFs/PDFSwiper";
import { pdfjs } from 'react-pdf';
import { BiInfoCircle } from "react-icons/bi";
import SnackbarAlert from '../../common/SnackbarAlert';
import MantineEditorWrapper from '../../common/MantineRichTextEditor/MantineEditorWrapper';
import MantineRichTextEditor from '../../common/MantineRichTextEditor/MantineRichTextEditor';
import { formatDateTimeYMD } from '../../../i18n/getIntlLocale';
import { normalizeExternalHref } from '../../../utils/normalizeExternalHref';

const paths = require('../../../paths.json');

// Set the worker source for pdfjs
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;


const DarkTooltip = styled(({ className, ...props }) => (
	<Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: '#222222 !important',
		color: '#ffffff',
		boxShadow: theme.shadows[1],
		fontSize: 11,
		margin: '0px'
	},
}));

const PREVIEWABLE_FILE_TYPES = ['img', 'vid', 'vid-rec', 'vey', 'vel', 'vev', 'vtw', 'vwi', 'aud', 'aud-rec', 'pdf', 'gos', 'god', 'mgm', 'mzm', 'mmt', 'mot'];

// Helper function to capitalize category names
const capitalizeCategory = (category) => {
	if (!category || category === 'all') return category;
	return category.split(' ').map(word => 
		word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
	).join(' ');
};

// Treat empty HTML (e.g. <p></p>) as empty for plain textarea display
const emptyHtmlToEmpty = (val) => {
	if (val == null || typeof val !== 'string') return '';
	const trimmed = val.trim();
	if (trimmed === '') return '';
	const stripped = trimmed.replace(/<[^>]*>/g, '').trim();
	return stripped === '' ? '' : val;
};

const PDFPost = React.memo(function PDFPost({ pdf_src, pre_upload, fullscreen, content_width_type }) {

	return (
		<div style={{ position: 'relative' }}>
			<PDFSwiper pdfFile={pre_upload || fullscreen ? pdf_src : paths.CONTENT_CLOUDFRONT + pdf_src} content_width_type={content_width_type} />
		</div>
	)
})

const GoogleSheetsPost = React.memo(function GoogleSheetsPost({ video_src, type, handleFullscreenOpen, fullscreen }) {
	return (
		<div
			className="video-container"
			style={{
				borderRadius: '8px !important',
				overflow: 'hidden',
				height: 'calc(100vh - 40px)', margin: 'auto 0px 0px 0px', width: '100%'
			}}>
			<div
				className="video-container"
				style={{
					width: '100%',
					height: '100vh',
					overflow: 'hidden',
				}}>
				<div style={{
					width: '100%', height: '100vh',
					borderRadius: '8px', overflow: 'hidden'
				}}>
					<iframe
						id={'google_sheet_' + type}
						src={video_src}
						// src={"https://docs.google.com/spreadsheets/d/1uGFgOzX7fqJKrzbNz4fnhLpy89w7QTKl_24TUImCjXE/pubhtml?widget=true&amp;headers=false"}
						style={
							{
								display: 'block',
								position: 'absolute',
								top: '0',
								left: '0',
								width: '100%',
								height: '100%',
							}
						}
					></iframe>
					<a className={'hover_modal_icon'}
						onClick={() => {
							handleFullscreenOpen(video_src, 'gos')
						}}
					>
						{
							fullscreen ?
								null
								:
								<RiFullscreenFill className="fullscreen_icon" style={{ width: '20px', height: '20px', bottom: '10px', right: '10px' }} />
						}
					</a>
				</div>
			</div>
		</div>
	)
})


const GoogleDocsPost = React.memo(function GoogleDocsPost({ video_src, type, handleFullscreenOpen, fullscreen }) {
	return (
		<div
			className="video-container"
			style={{
				borderRadius: '8px !important',
				overflow: 'hidden',
				height: 'calc(100vh - 40px)', margin: 'auto 0px 0px 0px', width: '100%'
			}}>
			<div
				className="video-container"
				style={{
					width: '100%',
					height: '100vh',
					overflow: 'hidden',
				}}>
				<div style={{
					width: '100%', height: '100vh',
					borderRadius: '8px', overflow: 'hidden'
				}}>
					<iframe
						id={'google_sheet_' + type}
						src={video_src}

						// src={"https://docs.google.com/spreadsheets/d/1uGFgOzX7fqJKrzbNz4fnhLpy89w7QTKl_24TUImCjXE/pubhtml?widget=true&amp;headers=false"}
						style={
							{
								display: 'block',
								position: 'absolute',
								top: '0',
								left: '0',
								width: '100%',
								height: '100%',
							}
						}
					></iframe>
					<a className={'hover_modal_icon'}
						onClick={() => {
							handleFullscreenOpen(video_src, 'god')
						}}
					>
						{
							fullscreen ?
								null
								:
								<RiFullscreenFill className="fullscreen_icon" style={{ width: '20px', height: '20px', bottom: '10px', right: '10px' }} />
						}
					</a>
				</div>
			</div>
		</div>
	)
})

function CommunityComposer(props) {
	const { t } = useTranslation();
	const {
		windowWidth,
		currentUser,
		currentUserIcon,
		placeholder,
		textValue,
		onTextChange,
		onTextLengthChange,
		onAttachImage,
		selectedPreviewUrl,
		fileType,
		onRemoveAttachment,
		onSubmit,
		disabled,
		brandColor,
		scheduleDate,
		onScheduleDateChange,
		// Handler functions
		handleClickVideo,
		handleCloseVideo,
		handleShareVideo,
		handleClickAudio,
		handleCloseAudio,
		handleShareAudio,
		handleClickDoc,
		handleCloseDoc,
		handleSharePDF,
		handleClick,
		handleClose,
		getDevices,
		handleRemoveContent,
		handleFullscreenOpen,
		// Menu anchor elements
		anchorElVideo,
		anchorElAudio,
		anchorElDoc,
		anchorEl,
		// State setters
		setFileType,
		setIsMediaAttached,
		setSelectedPreviewUrl,
		setEmbedUrlType,
		setEmbedUrlIsOpen,
		setSnackbarEmbed,
		setDocModalOpen,
		setDocType,
		setSnackbarOpenDoc,
		setMeetingModalOpen,
		setMeetingType,
		setSnackbarOpenMeeting,
		// Recording props
		recordingStatus,
		setRecordingStatus,
		recordingDurationMins,
		setRecordingDurationMins,
		recordingDurationSeconds,
		setRecordingDurationSeconds,
		recordingDevices,
		setRecordingDevices,
		handleAudioDeviceChange,
		mediaRecorder,
		mediaStream,
		mediaDevices,
		setADevID,
		aDevID,
		changingDevice,
		setChangingDevice,
		localStream,
		videoBitsPerSecond,
		handleVideoDeviceChange,
		setVDevID,
		vDevID,
		recordedFileFormat,
		setRecordedFileFormat,
		saveFile,
		setSelectedFile,
		setShowPostButton,
		embedUrlKey,
		is_me,
		// Additional state
		meetingLink,
		setMeetingLink,
		docLink,
		setDocLink,
		embedVideoUrl,
		setEmbedVideoUrl,
		postMeetingTime,
		setPostMeetingTime,
		meetingModalOpen,
		docModalOpen,
		embedUrlIsOpen,
		processEmbedUrlSet,
		embedUrlType,
		setEmbedVideoUrlThread,
		setEmbedVideoUrlComment,
		setEmbedVideoUrlEdit,
		snackbarMsgEmbed,
		snackbarEmbed,
		embedVideoUrlThread,
		embedVideoUrlComment,
		embedVideoUrlEdit,
		docType,
		activeDocFileType,
		setActiveDocFileType,
		setFileTypeThread,
		setDocLinkThread,
		setFileTypeComment,
		setDocLinkComment,
		setDocLinkEdit,
		setFileTypeEdit,
		snackbarMsgDoc,
		snackbarOpenDoc,
		fileTypeThread,
		fileTypeComment,
		fileTypeEdit,
		docLinkThread,
		docLinkComment,
		docLinkEdit,
		checkDocLink,
		meetingType,
		setMeetingLinkThread,
		setMeetingLinkComment,
		setMeetingLinkEdit,
		setMeetingImageEdit,
		setMeetingImageFileNameEdit,
		meetingLinkThread,
		meetingLinkComment,
		meetingLinkEdit,
		meetingImageEdit,
		meetingImageFileNameEdit,
		snackbarMsgMeeting,
		snackbarOpenMeeting,
		snackbarSeverityMeeting,
		snackbarColorMeeting,
		checkMeetingLink,
		// Category props
		currentTabInfo,
		uploadPostTags,
		setUploadPostTags,
		addCommunityCategory,
		editCategoryModalOpen,
		onEditCategory,
		setSelectedFileEdit,
		pendingCategories,
		setPendingCategories
	} = props;

	const isImagePreview = fileType === 'img';
	const isVideoPreview = fileType === 'vid' || fileType === 'vid-rec';
	const isEmbedPreview = ['vey', 'vel', 'vev', 'vtw', 'vwi'].includes(fileType);
	const isAudioPreview = fileType === 'aud' || fileType === 'aud-rec';
	const isPDFPreview = fileType === 'pdf';
	const isGoogleDocPreview = fileType === 'gos' || fileType === 'god';
	const isMeetingPreview = ['mgm', 'mzm', 'mmt', 'mot'].includes(fileType);
	// Optional image attachment for event/meeting posts
	const [advancedEditor, setAdvancedEditor] = useState(false);
	const [eventImagePreviewUrl, setEventImagePreviewUrl] = useState('');
	const [eventImageFileName, setEventImageFileName] = useState('');
	
	const [pdfPreviewImage, setPdfPreviewImage] = useState(null);
	const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);

	// Schedule post state
	const [scheduleAnchorEl, setScheduleAnchorEl] = useState(null);
	const [scheduleDateError, setScheduleDateError] = useState('');

	// Category selection state
	const [anchorElCategory, setAnchorElCategory] = useState(null);
	const [categoryModalOpen, setCategoryModalOpen] = useState(false);
	const [categoryModalAnimating, setCategoryModalAnimating] = useState(false);
	const [newCategoryName, setNewCategoryName] = useState('');
	const [snackbarCategory, setSnackbarCategory] = useState(false);
	const [snackbarMsgCategory, setSnackbarMsgCategory] = useState('');
	const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
	const [showMobileInfoPopup, setShowMobileInfoPopup] = useState(false);
	
	// Mobile composer modal state
	const [composerModalOpen, setComposerModalOpen] = useState(false);
	const [modalAnimating, setModalAnimating] = useState(false);
	
	// Modal animation states
	const [meetingModalAnimating, setMeetingModalAnimating] = useState(false);
	const [docModalAnimating, setDocModalAnimating] = useState(false);
	const [embedModalAnimating, setEmbedModalAnimating] = useState(false);

	useEffect(() => {
		return () => {
			if (eventImagePreviewUrl) {
				URL.revokeObjectURL(eventImagePreviewUrl);
			}
		};
	}, [eventImagePreviewUrl]);

	useEffect(() => {
		// Reset the event image preview when the composer is cleared.
		if (fileType === 'txt' && eventImagePreviewUrl) {
			URL.revokeObjectURL(eventImagePreviewUrl);
			setEventImagePreviewUrl('');
			setEventImageFileName('');
		}
	}, [fileType, eventImagePreviewUrl]);

	// Set default category: first available category if exists, otherwise 'all'
	useEffect(() => {
		const currentTag = uploadPostTags && uploadPostTags.length > 0 ? uploadPostTags[0] : null;
		
		// Filter out null/empty values from tags
		const availableTags = (currentTabInfo?.tags || []).filter(tag => tag != null && tag !== '');
		
		// Include pending category in available tags if it exists
		const pendingTag = pendingCategories && pendingCategories.length > 0 ? pendingCategories[0] : null;
		const allAvailableTags = pendingTag && !availableTags.includes(pendingTag)
			? [...availableTags, pendingTag]
			: availableTags;
		
		// If no tag is selected or tag is empty
		if (!currentTag || currentTag === '') {
			// If there are categories available, default to the first one
			if (allAvailableTags.length > 0) {
				setUploadPostTags([allAvailableTags[0]]);
			} else {
				// Otherwise, default to 'all'
				setUploadPostTags(['all']);
			}
		} else if (currentTag !== 'all') {
			// If a specific category is selected, verify it still exists (including pending)
			if (allAvailableTags.length > 0 && !allAvailableTags.includes(currentTag)) {
				// Selected category no longer exists, reset to first available category
				setUploadPostTags([allAvailableTags[0]]);
			}
		}
	}, [uploadPostTags, setUploadPostTags, currentTabInfo, pendingCategories]);

	// Load first page of PDF for preview
	useEffect(() => {
		const loadPdfFirstPage = async () => {
			if (isPDFPreview && selectedPreviewUrl) {
				try {
					setPdfPreviewLoading(true);
					const pdfUrl = selectedPreviewUrl;
					const pdf = await pdfjs.getDocument(pdfUrl).promise;
					const page = await pdf.getPage(1);
					const scale = 0.5; // Lower scale for thumbnail
					const viewport = page.getViewport({ scale });
					const canvas = document.createElement('canvas');
					const context = canvas.getContext('2d');
					canvas.height = viewport.height;
					canvas.width = viewport.width;

					await page.render({ canvasContext: context, viewport }).promise;
					const imageData = canvas.toDataURL('image/png');
					setPdfPreviewImage(imageData);
					setPdfPreviewLoading(false);
				} catch (error) {
					console.error('Error loading PDF preview:', error);
					setPdfPreviewLoading(false);
					setPdfPreviewImage(null);
				}
			} else {
				setPdfPreviewImage(null);
			}
		};

		loadPdfFirstPage();
	}, [isPDFPreview, selectedPreviewUrl]);
	
	// Trigger animations when modals open
	useEffect(() => {
		if (meetingModalOpen) {
			setTimeout(() => {
				setMeetingModalAnimating(true);
			}, 10);
		} else {
			setMeetingModalAnimating(false);
		}
	}, [meetingModalOpen]);
	
	useEffect(() => {
		if (docModalOpen) {
			setTimeout(() => {
				setDocModalAnimating(true);
			}, 10);
		} else {
			setDocModalAnimating(false);
		}
	}, [docModalOpen]);
	
	useEffect(() => {
		if (embedUrlIsOpen) {
			setTimeout(() => {
				setEmbedModalAnimating(true);
			}, 10);
		} else {
			setEmbedModalAnimating(false);
		}
	}, [embedUrlIsOpen]);
	
	const embedPreviewSrc = React.useMemo(() => {
		if (!isEmbedPreview) return '';
		if (fileType === 'vey') {
			return embedUrlKey ? `https://www.youtube-nocookie.com/embed/${embedUrlKey}?rel=0&modestbranding=1` : selectedPreviewUrl;
		}
		if (fileType === 'vel') {
			return embedUrlKey ? `https://www.loom.com/embed/${embedUrlKey}?autoplay=false` : selectedPreviewUrl;
		}
		if (fileType === 'vev') {
			return embedUrlKey ? `https://player.vimeo.com/video/${embedUrlKey}` : selectedPreviewUrl;
		}
		if (fileType === 'vtw') {
			if (!embedUrlKey) return selectedPreviewUrl;
			const twitchSrc = embedUrlKey.startsWith('ch')
				? `https://player.twitch.tv/?channel=${embedUrlKey.slice(2)}&parent=${window.location.hostname}`
				: embedUrlKey.startsWith('c')
				? `https://clips.twitch.tv/embed?clip=${embedUrlKey.slice(1)}&parent=${window.location.hostname}`
				: `https://player.twitch.tv/?video=${embedUrlKey.slice(1)}&parent=${window.location.hostname}`;
			return twitchSrc;
		}
		if (fileType === 'vwi') {
			return embedUrlKey ? `https://fast.wistia.net/embed/iframe/${embedUrlKey}` : selectedPreviewUrl;
		}
		return '';
	}, [isEmbedPreview, fileType, embedUrlKey, selectedPreviewUrl]);

	const previewMediaSrc = isEmbedPreview ? embedPreviewSrc : selectedPreviewUrl;
	const hasPreview = Boolean(previewMediaSrc || selectedPreviewUrl) && PREVIEWABLE_FILE_TYPES.includes(fileType);

	const renderInlinePreview = () => {
		if (!hasPreview) return null;
		if (isImagePreview && selectedPreviewUrl) {
			return (
				<img
					src={selectedPreviewUrl}
					alt={t('nav.selected')}
					style={{
						width: '100%',
						height: '100%',
						objectFit: 'cover',
						borderRadius: '12px',
						display: 'block'
					}}
				/>
			);
		}
		if (isVideoPreview && selectedPreviewUrl) {
			return (
				<video
					src={selectedPreviewUrl}
					controls
					playsInline
					preload="metadata"
					style={{
						width: '100%',
						height: '100%',
						objectFit: 'cover',
						display: 'block'
					}}
				/>
			);
		}
		if (isEmbedPreview && embedPreviewSrc) {
			return (
				<iframe
					title={t('common.embedPreview', 'Embed preview')}
					src={embedPreviewSrc}
					frameBorder="0"
					allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
					allowFullScreen
					loading="lazy"
					referrerPolicy="strict-origin-when-cross-origin"
					style={{
						width: '100%',
						height: '100%',
						border: 'none',
						pointerEvents: 'none'
					}}
				/>
			);
		}
		if (isAudioPreview && selectedPreviewUrl) {
			return (
				<div style={{
					width: '100%',
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					backgroundColor: '#fff',
					borderRadius: '12px',
					padding: '8px'
				}}>
					<ReactAudioPlayer
						className='react-player'
						src={selectedPreviewUrl}
						controls
						style={{ 
							width: '100%', 
							height: '32px',
							outline: 'none'
						}}
					/>
				</div>
			);
		}
		if (isPDFPreview && selectedPreviewUrl) {
			if (pdfPreviewLoading) {
				return (
					<div style={{
						width: '100%',
						height: '100%',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						backgroundColor: '#f8f9fa',
						borderRadius: '12px',
						padding: '16px'
					}}>
						<TbFileText style={{ fontSize: '48px', color: '#dc3545', marginBottom: '8px' }} />
						<span style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>{t('community.composer.loading')}</span>
					</div>
				);
			}
			
			if (pdfPreviewImage) {
				return (
					<img
						src={pdfPreviewImage}
						alt={t('common.pdfPreview')}
						style={{
							width: '100%',
							height: '100%',
							objectFit: 'cover',
							borderRadius: '12px',
							display: 'block'
						}}
					/>
				);
			}
			
			// Fallback to icon if image failed to load
			return (
				<div style={{
					width: '100%',
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					backgroundColor: '#f8f9fa',
					borderRadius: '12px',
					padding: '16px'
				}}>
					<TbFileText style={{ fontSize: '48px', color: '#dc3545', marginBottom: '8px' }} />
					<span style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>{t('community.composer.pdfDocument')}</span>
				</div>
			);
		}
		if (isGoogleDocPreview && selectedPreviewUrl) {
			return (
				<div style={{
					width: '100%',
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					backgroundColor: '#FFFFFF',
					borderRadius: '12px',
					padding: '8px'
				}}>
					{fileType === 'gos' && <img src={GoogleSheetsImage} alt={t('dashboard.paidDms.googleSheets')} style={{ width: 32, height: 32, marginBottom: '4px' }} />}
					{fileType === 'god' && <img src={GoogleDocsImage} alt={t('dashboard.paidDms.googleDocs')} style={{ width: 32, height: 32, marginBottom: '4px' }} />}
					<span style={{ color: '#6C89AF', fontSize: 11, fontWeight: 500 }}>
						{fileType === 'gos' ? 'Sheets' : 'Docs'}
					</span>
				</div>
			);
		}
		if (isMeetingPreview && selectedPreviewUrl) {
			const getMeetingBackgroundColor = () => {
				if (fileType === 'mgm') return '#FFF6DC'; // Google Meet
				if (fileType === 'mzm') return '#E5F1FF'; // Zoom
				if (fileType === 'mmt') return '#E6E7FF'; // Microsoft Teams
				return '#F9FBFF'; // Default for 'mot' (Other Meeting)
			};

			if (eventImagePreviewUrl) {
				return (
					<img src={eventImagePreviewUrl} alt={t('community.post.event')} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }} />
				);
			}

			return (
				<div style={{
					width: '100%',
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					backgroundColor: getMeetingBackgroundColor(),
					borderRadius: '12px',
					padding: '8px'
				}}>
					{fileType === 'mzm' && <img src={ZoomImage} alt={t('profile.zoom')} style={{ width: 80, height: 'auto', objectFit: 'contain' }} />}
					{fileType === 'mgm' && <img src={GoogleMeetImage} alt={t('community.composer.googleMeet')} style={{ width: 'auto', height: 60, objectFit: 'contain' }} />}
					{fileType === 'mmt' && <img src={MicrosoftTeams} alt={t('community.composer.microsoftTeams')} style={{ width: 'auto', height: 48, objectFit: 'contain' }} />}
					{fileType === 'mot' && <TbLink style={{ color: '#6C89AF', fontSize: 32 }} />}
				</div>
			);
		}
		return null;
	};

	// Modal handlers
	const openComposerModal = () => {
		if (props.isUnlockedCommunityPreview) {
			return;
		}
		setComposerModalOpen(true);
		setTimeout(() => {
			setModalAnimating(true);
		}, 10);
	};

	const closeComposerModal = () => {
		setShowMobileInfoPopup(false);
		setModalAnimating(false);
		setTimeout(() => {
			setComposerModalOpen(false);
		}, 300);
	};

	const handleSubmit = () => {
		onSubmit();
		// Close modal on mobile after submission
		if (windowWidth < 600) {
			closeComposerModal();
		}
	};

	// Function to render the full composer content
	const renderFullComposer = (isModal = false) => (
		<div
			id="community-composer"
			style={{
				backgroundColor: 'var(--rebranding-content-background-color)',
				border: (isModal && windowWidth < 600) ? 'none' : '#E4EBF6 1px solid',
				borderRadius: '12px',
				boxSizing: 'border-box',
				padding: (isModal && windowWidth < 600) ? 0 : (windowWidth < 600 ? 12 : 20),
				position: 'relative',
				boxShadow: '0 1px 6px rgba(110,137,175,0.04)',
			}}
		>
			<div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', boxSizing: 'border-box' }}>
				<Avatar alt={currentUser} src={currentUserIcon} style={{ marginTop: 2 }} />
				<div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 12, boxSizing: 'border-box' }}>
					<div style={{ display: 'flex', width: '100%', gap: 12, boxSizing: 'border-box' }}>
					<div style={{ flex: hasPreview && windowWidth >= 600 ? 1.2 : 1, boxSizing: 'border-box' }}>
						{is_me && advancedEditor ? (
							<Box
								id="communityComposerInput"
								sx={{
									backgroundColor: 'white',
									borderRadius: '8px',
									overflow: 'visible'
								}}
							>
								<MantineEditorWrapper>
									<MantineRichTextEditor
										value={textValue || ''}
										onChange={(value) => {
											onTextChange(value)
											onTextLengthChange(value.length)
										}}
										placeholder={t('community.placeholders.addContent')}
										minHeight={200}
										maxHeight={300}
										maxLength={25000}
									/>
								</MantineEditorWrapper>
							</Box>
						) : (
							<>
								<TextareaAutosize
									id="communityComposerInput"
									minRows={3}
									maxRows={10}
									className={"community-inputs"}
									style={{
										border: 'none',
										display: 'block',
										fontWeight: '500',
										fontSize: 16,
										padding: '10px 0px',
										boxSizing: 'border-box',
										color: '#1D1D1D',
										borderRadius: 0,
										width: '100%',
										fontFamily: 'Roboto',
										resize: 'none',
										outline: 'none',
										backgroundColor: 'transparent',
										overflow: 'auto',
									}}
									disabled={disabled}
									placeholder={is_me ? t('community.placeholders.addContent') : placeholder}
									maxLength={5000}
									value={emptyHtmlToEmpty(textValue)}
									onChange={(e) => onTextChange(e.target.value)}
								/>
								</>
						)}
					</div>

						{hasPreview && windowWidth >= 600 && (
							<div style={{ marginRight: '0px', marginLeft: 'auto' }}>
								<div
									style={{
										width: '100%',
										maxWidth: '180px',
										aspectRatio: '16/9',
										minHeight: '100px',
										maxHeight: '101.25px',
										position: 'relative',
										borderRadius: '12px',
										overflow: 'hidden',
										background: 'rgb(243, 243, 243)',
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
									}}
								>
									{renderInlinePreview()}
									<div style={{ position: 'absolute', top: 8, right: 8, cursor: 'pointer', zIndex: 10 }}>
										<IconButton onClick={onRemoveAttachment} size="small"
											sx={{ border: '1px solid #D3DCE7', backgroundColor: '#fff', p: '5px', height: '28px', width: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
											<IoClose style={{ fontSize: '16px', color: '#222' }} />
										</IconButton>
									</div>
								</div>
							</div>
						)}
					</div>

					{/* Preview below text area (mobile only) */}
					{hasPreview && windowWidth < 600 && (
						<div style={{ width: '100%' }}>
							<div
								style={{
									width: '100%',
									aspectRatio: '16/9',
									position: 'relative',
									borderRadius: '12px',
									overflow: 'hidden',
									background: 'rgb(243, 243, 243)',
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
								}}
							>
								{renderInlinePreview()}
								<div style={{ position: 'absolute', top: 8, right: 8, cursor: 'pointer', zIndex: 10 }}>
									<IconButton onClick={onRemoveAttachment} size="small"
										sx={{ border: '1px solid #D3DCE7', backgroundColor: '#fff', p: '5px', height: '28px', width: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
										<IoClose style={{ fontSize: '16px', color: '#222' }} />
									</IconButton>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>

			<div
				style={{
					display: 'flex',
					flexDirection: windowWidth < 600 ? 'column' : 'row',
					alignItems: windowWidth < 600 ? 'stretch' : 'center',
					justifyContent: 'space-between',
					marginTop: '8px',
					gap: windowWidth < 600 ? '12px' : '0',
				}}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 8,
						marginLeft: '0px',
						flexWrap: 'wrap',
					}}
				>
					<span
						onClick={onAttachImage}
						style={{
							display: 'inline-flex',
							alignItems: 'center',
							cursor: 'pointer',
							borderRadius: '6px',
							transition: 'background 0.15s',
							padding: '4px'
						}}
						onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f1f1'}
						onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
					>
						<TbPhoto style={{ color: '#666666', fontSize: 22 }} />
					</span>
					<DarkTooltip title={t('community.composer.uploadOrEmbedVideo')} placement="bottom">
						<span
							onClick={handleClickVideo}
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								cursor: 'pointer',
								borderRadius: '6px',
								transition: 'background 0.15s',
								padding: '4px'
							}}
							onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f1f1'}
							onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
						>
							<TbVideo style={{ color: '#666666', fontSize: 22 }} />
						</span>
					</DarkTooltip>
					<Menu
						id="simple-menu"
						anchorEl={anchorElVideo}
						keepMounted
						disablePortal={false}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'center',
						}}
						transformOrigin={{
							vertical: 'top',
							horizontal: 'center',
						}}
						open={Boolean(anchorElVideo)}
						onClose={handleCloseVideo}
						ModalProps={{
							disableAutoFocus: true,
							disableEnforceFocus: true,
							disableRestoreFocus: true,
						}}
						sx={{
							zIndex: 10001,
							"& .MuiPaper-root": {
								backgroundColor: "#fff",
								boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 12px 0px, rgba(229, 229, 229, 0.05) 0px 0px 0px 1px;',
								border: '1px #f4f4f4 solid',
								zIndex: 10001
							}
						}}
					>
						<div>
							<MenuItem type="button" onClick={() => {
								handleCloseVideo();
								handleShareVideo();
							}}
								sx={{ color: 'black', fontWeight: '500', fontSize: '14px' }}>
								<TbVideo
									style={{ verticalAlign: 'middle', width: '16px', height: '16px', color: '#6C89AF', marginRight: '8px' }} />
								{t('community.composer.upload')}
							</MenuItem>
							<MenuItem
								onClick={() => {
									handleCloseVideo();
									setEmbedUrlType('post');
									setEmbedUrlIsOpen(true);
									setSnackbarEmbed(false);
								}}
								sx={{ color: 'black', fontWeight: '500', fontSize: '14px' }}>
								<ImEmbed2
									style={{ verticalAlign: 'middle', width: '16px', height: '16px', color: '#6C89AF', marginRight: '8px' }} />
								{t('community.composer.embed')}
							</MenuItem>
						</div>
					</Menu>
					<DarkTooltip title={t('community.composer.uploadAudio')} placement="bottom">
						<span
							onClick={() => {
								handleCloseVideo();
								handleShareAudio();
							}}
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								cursor: 'pointer',
								borderRadius: '6px',
								transition: 'background 0.15s',
								padding: '4px'
							}}
							onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f1f1'}
							onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
						>
							<TbMicrophone style={{ color: '#666666', fontSize: 22 }} />
						</span>
					</DarkTooltip>
					<DarkTooltip title={t('community.composer.uploadDoc')} placement="bottom">
						<span
							onClick={handleClickDoc}
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								cursor: 'pointer',
								borderRadius: '6px',
								transition: 'background 0.15s',
								padding: '4px'
							}}
							onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f1f1'}
							onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
						>
							<TbFile style={{ color: '#666666', fontSize: 22 }} />
						</span>
					</DarkTooltip>
					<Menu
						id="simple-menu"
						anchorEl={anchorElDoc}
						keepMounted
						disablePortal={false}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'center',
						}}
						transformOrigin={{
							vertical: 'top',
							horizontal: 'center',
						}}
						open={Boolean(anchorElDoc)}
						onClose={handleCloseDoc}
						ModalProps={{
							disableAutoFocus: true,
							disableEnforceFocus: true,
							disableRestoreFocus: true,
						}}
						sx={{
							zIndex: 10001,
							"& .MuiPaper-root": {
								backgroundColor: "#fff",
								boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 12px 0px, rgba(229, 229, 229, 0.05) 0px 0px 0px 1px;',
								border: '1px #f4f4f4 solid',
								zIndex: 10001
							}
						}}
					>
						<div>
							<MenuItem type="button" onClick={() => {
								handleCloseDoc();
								handleSharePDF();
							}}
								sx={{ color: 'black', fontWeight: '500', fontSize: '14px' }}>
								<FaFilePdf
									style={{ verticalAlign: 'middle', color: '#6C89AF', marginRight: '8px' }} />
								{t('community.composer.uploadPdf')}
							</MenuItem>
							<MenuItem
								onClick={() => {
									handleCloseDoc();
									setDocModalOpen(true);
									setFileType('gos');
									setDocType('post');
									setSnackbarOpenDoc(false);
								}}
								sx={{ color: 'black', fontWeight: '500', fontSize: '14px' }}>
								<img src={GoogleSheetsImage}
									style={{ verticalAlign: 'middle', color: '#6C89AF', width: '16px', height: '16px', marginRight: '8px' }} />
								{t('community.composer.googleSheet')}
							</MenuItem>
							<MenuItem
								onClick={() => {
									handleCloseDoc();
									setDocModalOpen(true);
									setFileType('god');
									setDocType('post');
									setSnackbarOpenDoc(false);
								}}
								sx={{ color: 'black', fontWeight: '500', fontSize: '14px' }}>
								<img src={GoogleDocsImage}
									style={{ verticalAlign: 'middle', color: '#6C89AF', width: '16px', height: '16px', marginRight: '8px' }} />
								{t('community.composer.googleDoc')}
							</MenuItem>
						</div>
					</Menu>
					{is_me && (
						<>
							<DarkTooltip title={t('community.composer.addMeeting')} placement="bottom">
								<span
									className="calendar-meeting-button"
									aria-label={t('community.ariaLabels.addMeeting')}
									onClick={handleClick}
									style={{
										display: 'inline-flex',
										alignItems: 'center',
										cursor: 'pointer',
										borderRadius: '6px',
										transition: 'background 0.15s',
										padding: '4px'
									}}
									onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f1f1'}
									onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
								>
									<TbCalendar style={{ color: '#666666', fontSize: 22 }} />
								</span>
							</DarkTooltip>
							<DarkTooltip title={t('community.composer.schedulePost')} placement="bottom">
								<span
									className="schedule-post-button"
									aria-label={t('community.composer.schedulePost')}
									onClick={(e) => setScheduleAnchorEl(e.currentTarget)}
									style={{
										display: 'inline-flex',
										alignItems: 'center',
										cursor: 'pointer',
										borderRadius: '6px',
										transition: 'background 0.15s',
										padding: '4px'
									}}
									onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f1f1'}
									onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
								>
									<TbClock style={{ color: scheduleDate ? (brandColor || 'var(--brand-color)') : '#666666', fontSize: 22 }} />
								</span>
							</DarkTooltip>
							<Popover
								open={Boolean(scheduleAnchorEl)}
								anchorEl={scheduleAnchorEl}
								onClose={() => setScheduleAnchorEl(null)}
								anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
								transformOrigin={{ vertical: 'top', horizontal: 'center' }}
								sx={{ zIndex: 10001 }}
							>
								<div style={{ padding: '16px', minWidth: '260px' }}>
									<p style={{ color: '#000', fontWeight: '600', fontSize: '14px', margin: '0 0 10px 0' }}>
										{t('community.composer.selectScheduleDate')}
									</p>
									<input
										type="datetime-local"
										style={{
											border: '1px solid #D9DADB',
											borderRadius: '6px',
											padding: '8px',
											width: '100%',
											boxSizing: 'border-box',
											fontFamily: 'Roboto',
											fontSize: '14px',
											color: '#3B55F3',
										}}
										value={scheduleDate ? (() => {
											const localDate = new Date(scheduleDate);
											if (isNaN(localDate.getTime())) return '';
											const year = localDate.getFullYear();
											const month = String(localDate.getMonth() + 1).padStart(2, '0');
											const day = String(localDate.getDate()).padStart(2, '0');
											const hours = String(localDate.getHours()).padStart(2, '0');
											const minutes = String(localDate.getMinutes()).padStart(2, '0');
											return `${year}-${month}-${day}T${hours}:${minutes}`;
										})() : ''}
										min={(() => {
											const now = new Date();
											const year = now.getFullYear();
											const month = String(now.getMonth() + 1).padStart(2, '0');
											const day = String(now.getDate()).padStart(2, '0');
											const hours = String(now.getHours()).padStart(2, '0');
											const minutes = String(now.getMinutes()).padStart(2, '0');
											return `${year}-${month}-${day}T${hours}:${minutes}`;
										})()}
										onChange={(event) => {
											const localDate = new Date(event.target.value);
											if (isNaN(localDate.getTime())) {
												onScheduleDateChange(null);
												return;
											}
											if (localDate < new Date()) {
												setScheduleDateError(t('community.composer.scheduleDatePast'));
												return;
											}
											setScheduleDateError('');
											onScheduleDateChange(localDate.toISOString());
										}}
									/>
									<p style={{ color: '#999', fontSize: '11px', margin: '6px 0 0 0', fontStyle: 'italic' }}>
										{Intl.DateTimeFormat().resolvedOptions().timeZone}
									</p>
									{scheduleDateError && (
										<p style={{ color: 'var(--danger-color)', fontSize: '12px', margin: '8px 0 0 0' }}>
											{scheduleDateError}
										</p>
									)}
									{scheduleDate && (
										<Button
											fullWidth
											onClick={() => {
												onScheduleDateChange(null);
												setScheduleDateError('');
												setScheduleAnchorEl(null);
											}}
											sx={{ textTransform: 'none', color: 'var(--danger-color)', fontWeight: '600', marginTop: '10px' }}
										>
											{t('community.composer.clearSchedule')}
										</Button>
									)}
								</div>
							</Popover>
							<Menu
								id="simple-menu"
								anchorEl={anchorEl}
								keepMounted
								disablePortal={false}
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'center',
								}}
								transformOrigin={{
									vertical: 'top',
									horizontal: 'center',
								}}
								open={Boolean(anchorEl)}
								onClose={handleClose}
								ModalProps={{
									disableAutoFocus: true,
									disableEnforceFocus: true,
									disableRestoreFocus: true,
								}}
								sx={{
									zIndex: 10001,
									"& .MuiPaper-root": {
										backgroundColor: "#fff",
										boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 12px 0px, rgba(229, 229, 229, 0.05) 0px 0px 0px 1px;',
										border: '1px #f4f4f4 solid',
										zIndex: 10001
									}
								}}
							>
								<div>
									<MenuItem type="button" onClick={() => {
										handleClose();
										setMeetingModalOpen(true);
										setFileType('mzm');
										setMeetingType('post');
										setSnackbarOpenMeeting(false);
									}}
										sx={{ color: 'black', fontWeight: '500', fontSize: '14px' }}>
										<img src={ZoomImage}
											style={{ verticalAlign: 'middle', height: 'auto', width: '16px', marginRight: '8px' }} />
										{t('community.composer.zoom')}
									</MenuItem>
									<MenuItem
										onClick={() => {
											handleClose();
											setMeetingModalOpen(true);
											setFileType('mgm');
											setMeetingType('post');
											setSnackbarOpenMeeting(false);
										}}
										sx={{ color: 'black', fontWeight: '500', fontSize: '14px' }}>
										<img src={GoogleMeetImage}
											style={{ verticalAlign: 'middle', height: 'auto', width: '16px', marginRight: '8px' }} />
										{t('community.composer.googleMeet')}
									</MenuItem>
									<MenuItem
										onClick={() => {
											handleClose();
											setMeetingModalOpen(true);
											setFileType('mmt');
											setMeetingType('post');
											setSnackbarOpenMeeting(false);
										}}
										sx={{ color: 'black', fontWeight: '500', fontSize: '14px' }}>
										<img src={MicrosoftTeams}
											style={{ verticalAlign: 'middle', height: 'auto', width: '16px', marginRight: '8px' }} />
										{t('community.composer.microsoftTeams')}
									</MenuItem>
									<MenuItem
										onClick={() => {
											handleClose();
											setMeetingModalOpen(true);
											setFileType('mot');
											setMeetingType('post');
											setSnackbarOpenMeeting(false);
										}}
										sx={{ color: 'black', fontWeight: '500', fontSize: '14px' }}>
										<TbLink
											style={{ verticalAlign: 'middle', color: '#6C89AF', width: '16px', height: '16px', marginRight: '8px' }} />
										{t('community.composer.other')}
									</MenuItem>
								</div>
							</Menu>
						</>
					)}

					{/* Advanced Editor Toggle */}
					{is_me && (
						<span
							onClick={() => setAdvancedEditor(prev => !prev)}
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								cursor: 'pointer',
								borderRadius: '6px',
								padding: '4px',
								color: advancedEditor ? (brandColor || '#4F6BED') : '#666',
								backgroundColor: 'transparent',
								border: 'none',
								transition: 'all 0.15s',
							}}
							onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f1f1'}
							onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
						>
							<RiFullscreenFill style={{ fontSize: 20 }} />
						</span>
					)}

					{/* Divider */}
					<div style={{ width: '1px', height: '24px', backgroundColor: '#D3DCE7', flexShrink: 0 }} />

					{/* Category Selector Dropdown */}
					<div style={{
						display: 'flex',
						alignItems: 'center',
						gap: '6px',
						flexShrink: 0,
					}}>
						<ClickAwayListener
							onClickAway={(e) => {
								// Don't close if clicking the edit button
								if (e.target && e.target.closest('.edit-category-btn')) {
									return;
								}
								setShowCategoryDropdown(false);
							}}
						>
							<div style={{
								position: 'relative',
								maxWidth: windowWidth < 600 ? 180 : 200,
								flexShrink: 0,
							}}>
							<motion.button
								whileTap={{ scale: 0.98 }}
								onClick={() => setShowCategoryDropdown((prev) => !prev)}
								style={{
									width: 'auto',
									maxWidth: '100%',
									zIndex: windowWidth < 600 ? 1500 : 100, // Lower than community tab dropdown
									background: 'white',
									border: showCategoryDropdown
										? `0px solid ${brandColor || '#6C89AF'}`
										: '0px solid #C6D1DE',
									borderRadius: '8px',
									margin: '0px auto',
									padding: '8px 14px',
									fontSize: '15px',
									fontWeight: 300,
									color: '#6B7280',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									cursor: 'pointer',
									// boxShadow: '0 1px 6px rgba(110,137,175,0.04)',
									transition: 'border 0.2s, box-shadow 0.2s',
									outline: 'none',
									position: 'relative',
									minHeight: 32,
									height: 32,
								}}
								// whileHover={{
								// 	boxShadow: "0 2px 8px rgba(110,137,175,0.05)",
								// }}
								aria-haspopup="listbox"
								aria-expanded={!!showCategoryDropdown}
							>
								<span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
									<span
										style={{
											fontWeight: 400,
											fontSize: '15px',
											color: '#6B7280',
											// color: (uploadPostTags && uploadPostTags[0] && uploadPostTags[0] !== 'all') ? '#1D1D1D' : '#666666',
											lineHeight: 1.2,
											maxWidth: windowWidth < 600 ? 120 : 150,
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
											display: 'inline-block',
											verticalAlign: 'middle',
										}}
										title={(uploadPostTags && uploadPostTags[0] && uploadPostTags[0] !== 'all') ? capitalizeCategory(uploadPostTags[0]) : t('community.composer.allCategories')}
									>
										{(uploadPostTags && uploadPostTags[0] && uploadPostTags[0] !== 'all') 
											? (capitalizeCategory(uploadPostTags[0]).length > 20 
												? capitalizeCategory(uploadPostTags[0]).substring(0, 20) + '...' 
												: capitalizeCategory(uploadPostTags[0])) 
											: t('community.composer.allCategories')}
									</span>
								</span>
								<motion.span
									animate={{ rotate: showCategoryDropdown ? 180 : 0 }}
									transition={{ duration: 0.25 }}
									style={{ display: 'flex', alignItems: 'center', marginLeft: '5px' }}
								>
									<TbChevronUp
										style={{
											fontSize: '18px',
											color: '#656565',
											transition: 'transform 0.2s',
										}}
									/>
									
								</motion.span>
							</motion.button>
							<AnimatePresence>
								{showCategoryDropdown && (
									<motion.ul
										initial={{ opacity: 0, y: -10 }}
										animate={{ opacity: 1, y: 0 }}
										exit={{ opacity: 0, y: -10 }}
										transition={{ duration: 0.25 }}
										style={{
											position: 'absolute',
											top: 'calc(100% + 6px)',
											left: 0,
											width: '100%',
											minWidth: windowWidth < 600 ? 180 : 200,
											background: 'white',
											borderRadius: '10px',
											boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
											padding: '6px 0',
											zIndex: windowWidth < 600 ? 1500 : 100, // Lower than community tab dropdown (2000 on mobile, 1000 on desktop/tablet)
											listStyle: 'none',
											margin: 0,
											border: '1px solid #E4EBF6',
											maxHeight: 300,
											overflowY: 'auto',
										}}
										role="listbox"
									>
										{/* Add New Category - hidden, now managed via Customize mode */}
										{false && is_me && (
											<motion.li
												onClick={(e) => {
													e.stopPropagation();
													setShowCategoryDropdown(false);
													setTimeout(() => {
														setCategoryModalOpen(true);
														setNewCategoryName('');
														setTimeout(() => {
															setCategoryModalAnimating(true);
														}, 10);
													}, 150);
												}}
												whileHover={{
													backgroundColor: 'rgba(110,137,175,0.04)',
												}}
												style={{
													padding: '10px 14px',
													cursor: 'pointer',
													display: 'flex',
													alignItems: 'center',
													fontWeight: 500,
													color: brandColor || '#6C89AF',
													fontSize: '14px',
													borderBottom: '1px solid #f4f4f4',
													marginBottom: '4px',
													transition: 'background 0.15s',
													gap: 8,
													minHeight: 36,
												}}
												role="option"
											>
												<span
													style={{
														flex: 1,
														display: 'flex',
														alignItems: 'center',
														gap: 6,
														minWidth: 0,
													}}
												>
													<TbPlus
														style={{
															width: '16px',
															height: '16px',
															marginRight: '8px',
														}}
													/>
													<span>{t('community.composer.newCategory')}</span>
												</span>
											</motion.li>
										)}
										<motion.li
											onClick={() => {
												setShowCategoryDropdown(false);
												setUploadPostTags(['all']);
											}}
											whileHover={{
												backgroundColor: (uploadPostTags && uploadPostTags[0] === 'all')
													? 'rgba(110,137,175,0.06)'
													: 'rgba(110,137,175,0.04)',
											}}
											style={{
												padding: '10px 14px',
												cursor: 'pointer',
												display: 'flex',
												alignItems: 'center',
												fontWeight: (uploadPostTags && uploadPostTags[0] === 'all') ? 600 : 500,
												color: (uploadPostTags && uploadPostTags[0] === 'all') ? (brandColor || '#6C89AF') : '#1D1D1D',
												fontSize: '14px',
												background: (uploadPostTags && uploadPostTags[0] === 'all')
													? 'rgba(110,137,175,0.06)'
													: 'transparent',
												boxShadow: (uploadPostTags && uploadPostTags[0] === 'all')
													? `0 1px 6px 0 rgba(110,137,175,0.04)`
													: 'none',
												transition: 'background 0.15s, color 0.15s, box-shadow 0.15s',
												gap: 8,
												minHeight: 36,
											}}
											role="option"
											aria-selected={(uploadPostTags && uploadPostTags[0] === 'all')}
										>
											<span
												style={{
													flex: 1,
													display: 'flex',
													alignItems: 'center',
													gap: 6,
													minWidth: 0,
												}}
											>
												<span
													style={{
														maxWidth: 200,
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														whiteSpace: 'nowrap',
														display: 'inline-block',
														verticalAlign: 'middle',
													}}
													title={t('community.composer.allCategories')}
												>
													{t('community.composer.allCategories')}
												</span>
											</span>
										</motion.li>
										{(() => {
											// Combine existing tags with pending category for display
											const existingTags = (currentTabInfo?.tags || []).filter(cat => cat != null && cat !== '');
											const pendingTag = pendingCategories && pendingCategories.length > 0 ? pendingCategories[0] : null;
											const allTags = pendingTag && !existingTags.includes(pendingTag)
												? [...existingTags, pendingTag]
												: existingTags;
											
											return allTags.length > 0 && (
												<>
													{allTags.map((cat, index) => {
													const isSelected = (uploadPostTags && uploadPostTags[0] === cat);
													const capitalizedCat = capitalizeCategory(cat) || '';
													const isPending = pendingTag === cat;
													return (
														<motion.li
															key={cat || `cat-${index}`}
															onClick={(e) => {
																// Don't do anything if clicking edit icon - let the button handle it
																if (e.target.closest('.edit-category-btn')) {
																	return;
																}
																setShowCategoryDropdown(false);
																setUploadPostTags([cat]);
															}}
															whileHover={{
																backgroundColor: isSelected
																	? 'rgba(110,137,175,0.06)'
																	: 'rgba(110,137,175,0.04)',
															}}
															style={{
																padding: '10px 14px',
																cursor: 'pointer',
																display: 'flex',
																alignItems: 'center',
																justifyContent: 'space-between',
																fontWeight: isSelected ? 600 : 500,
																color: isSelected ? (brandColor || '#6C89AF') : '#1D1D1D',
																fontSize: '14px',
																background: isSelected
																	? 'rgba(110,137,175,0.06)'
																	: 'transparent',
																boxShadow: isSelected
																	? `0 1px 6px 0 rgba(110,137,175,0.04)`
																	: 'none',
																transition: 'background 0.15s, color 0.15s, box-shadow 0.15s',
																gap: 8,
																minHeight: 36,
																			}}
															role="option"
															aria-selected={isSelected}
														>
															<span
																style={{
																	flex: 1,
																	display: 'flex',
																	alignItems: 'center',
																	gap: 6,
																	minWidth: 0,
																}}
															>
																<span
																	style={{
																		maxWidth: windowWidth < 600 ? 120 : 150,
																		overflow: 'hidden',
																		textOverflow: 'ellipsis',
																		whiteSpace: 'nowrap',
																		display: 'inline-block',
																		verticalAlign: 'middle',
																	}}
																	title={cat}
																>
																	{capitalizedCat.length > 20 
																		? capitalizedCat.substring(0, 20) + '...' 
																		: capitalizedCat}
																</span>
															</span>
															{is_me && !isPending && (
																<button
																	className="edit-category-btn"
																	type="button"
																	onClick={(e) => {
																		e.preventDefault();
																		e.stopPropagation();
																		// Keep dropdown open and trigger edit modal
																		if (onEditCategory) {
																			onEditCategory(cat);
																		} else {
																			console.warn('onEditCategory prop not provided');
																		}
																	}}
																	style={{
																		padding: '4px',
																		borderRadius: '50%',
																		border: 'none',
																		background: 'transparent',
																		cursor: 'pointer',
																		display: 'flex',
																		alignItems: 'center',
																		justifyContent: 'center',
																		color: '#666',
																		transition: 'background 0.15s, color 0.15s',
																		flexShrink: 0,
																		marginLeft: '8px',
																	}}
																	onMouseEnter={(e) => {
																		e.currentTarget.style.backgroundColor = 'rgba(110,137,175,0.1)';
																		e.currentTarget.style.color = brandColor || '#6C89AF';
																	}}
																	onMouseLeave={(e) => {
																		e.currentTarget.style.backgroundColor = 'transparent';
																		e.currentTarget.style.color = '#666';
																	}}
																>
																	<TbPencil style={{ fontSize: 14 }} />
																</button>
															)}
														</motion.li>
													);
												})}
											</>
											);
										})()}
									</motion.ul>
								)}
							</AnimatePresence>
						</div>
					</ClickAwayListener>
					{false && (windowWidth < 600 ? (
						<ClickAwayListener onClickAway={() => setShowMobileInfoPopup(false)}>
							<div style={{ position: 'relative' }}>
								<span
									style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center' }}
									onClick={() => setShowMobileInfoPopup(!showMobileInfoPopup)}
								>
									<BiInfoCircle style={{ fontSize: '16px', color: '#6E89AF', verticalAlign: 'middle' }} />
								</span>
								{showMobileInfoPopup && (
									<div
										style={{
											position: 'absolute',
											bottom: 'calc(100% + 8px)',
											right: 0,
											backgroundColor: '#222222',
											color: '#ffffff',
											padding: '12px 16px',
											borderRadius: '8px',
											fontSize: '12px',
											lineHeight: 1.5,
											width: '280px',
											boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
											zIndex: 10000,
											whiteSpace: 'normal',
											wordWrap: 'break-word',
										}}
									>
										{t('community.composer.newCategoryWarning')}
										<div
											style={{
												position: 'absolute',
												bottom: '-6px',
												right: '20px',
												width: 0,
												height: 0,
												borderLeft: '6px solid transparent',
												borderRight: '6px solid transparent',
												borderTop: '6px solid #222222',
											}}
										/>
									</div>
								)}
							</div>
						</ClickAwayListener>
					) : (
						<DarkTooltip
							title={t('community.composer.newCategoryWarning')}
							enterTouchDelay={0}
							placement="top"
						>
							<span style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center' }}>
								<BiInfoCircle style={{ fontSize: '16px', color: '#6E89AF', verticalAlign: 'middle' }} />
							</span>
						</DarkTooltip>
					))}
				</div>
			</div>

				<div
					style={{
						display: 'flex',
						flexDirection: windowWidth < 600 ? 'column' : 'row',
						alignItems: windowWidth < 600 ? 'stretch' : 'center',
						gap: '8px',
						width: windowWidth < 600 ? '100%' : undefined,
						marginLeft: windowWidth < 600 ? 0 : undefined,
						marginTop: windowWidth < 600 ? '8px' : 0,
					}}
				>
					{scheduleDate && (
						<Chip
							size="small"
							label={t('community.composer.scheduledFor', { date: formatDateTimeYMD(new Date(scheduleDate)) })}
							onDelete={() => onScheduleDateChange(null)}
							deleteIcon={<TbX />}
							sx={{ backgroundColor: '#EEF1FB', color: '#3B55F3', fontWeight: 600 }}
						/>
					)}
					<Button
						variant="contained"
						disableElevation
						fullWidth={windowWidth < 600}
						sx={{
							padding: windowWidth < 600 ? '12px 20px' : '6px 14px',
							textTransform: 'none',
							fontWeight: 600,
							borderRadius: '8px',
							backgroundColor: brandColor || 'var(--brand-color)',
							width: windowWidth < 600 ? '100%' : undefined,
							'&:hover': {
								backgroundColor: brandColor
									? (typeof window !== 'undefined'
										? (() => {
											// Compute lighter color on hover
											try {
												const hex = brandColor.replace('#', '');
												let num = parseInt(hex, 16);
												let r = (num >> 16) + 15;
												let g = ((num >> 8) & 0x00FF) + 15;
												let b = (num & 0x0000FF) + 15;
												r = Math.min(255, r);
												g = Math.min(255, g);
												b = Math.min(255, b);
												return `rgb(${r},${g},${b})`;
											} catch {
												return brandColor;
											}
										})()
										: brandColor)
									: 'var(--brand-color-hover)'
							},
						}}
						disabled={disabled}
						onClick={handleSubmit}
					>
						{scheduleDate ? t('community.composer.schedulePost') : t('community.composer.submitPost')}
					</Button>
				</div>
			</div>
		</div>
	);

	return (
		<>
			{/* Mobile: Simplified input that opens modal */}
			{windowWidth < 600 ? (
				<>
					<div
						style={{
							backgroundColor: 'var(--rebranding-content-background-color)',
							border: '#E4EBF6 1px solid',
							borderRadius: '12px',
							boxSizing: 'border-box',
							minHeight: 60,
							padding: '12px',
							position: 'relative',
							boxShadow: '0 1px 6px rgba(110,137,175,0.04)',
							cursor: 'pointer',
						}}
						onClick={openComposerModal}
					>
						<div style={{ display: 'flex', gap: 12, alignItems: 'center', boxSizing: 'border-box' }}>
							<Avatar alt={currentUser} src={currentUserIcon} style={{ marginTop: 2 }} />
							<TextareaAutosize
								readOnly
								minRows={1}
								className={"text-input community-inputs"}
								style={{
									border: 'none',
									display: 'block',
									fontWeight: '500',
									fontSize: 16,
									padding: '10px 12px',
									boxSizing: 'border-box',
									color: '#999',
									borderRadius: 8,
									width: '100%',
									fontFamily: 'Roboto',
									resize: 'none',
									backgroundColor: 'transparent',
									cursor: 'pointer',
								}}
								value=""
								placeholder={t('community.placeholders.clickToCreatePost')}
							/>
						</div>
					</div>

					{/* Mobile Composer Modal */}
					{composerModalOpen && (
						<Modal
							open={composerModalOpen}
							onClose={closeComposerModal}
							aria-labelledby="composer-modal-title"
							aria-describedby="composer-modal-description"
							style={{ zIndex: 9999, outline: '0' }}
						>
							<div
								style={{
									position: 'fixed',
									top: 0,
									left: 0,
									right: 0,
									bottom: 0,
									outline: '0',
									backgroundColor: 'rgba(0, 0, 0, 0.6)',
									display: 'flex',
									alignItems: 'flex-end',
									justifyContent: 'center',
									zIndex: 9999,
									padding: '0px',
									opacity: modalAnimating ? 1 : 0,
									transition: 'opacity 0.3s ease-in-out',
									pointerEvents: editCategoryModalOpen ? 'none' : 'auto'
								}}
								onClick={closeComposerModal}
							>
								<div
									style={{
										maxWidth: '100%',
										width: '100%',
										height: 'auto',
										maxHeight: '90vh',
										minHeight: '60vh',
										backgroundColor: '#fff',
										borderRadius: '20px 20px 0px 0px',
										position: 'relative',
										boxShadow: '0 -10px 40px rgba(0, 0, 0, 0.15)',
										overflow: 'visible',
										transform: modalAnimating 
											? 'translateY(0)' 
											: 'translateY(100%)',
										opacity: modalAnimating ? 1 : 0,
										transition: 'all 0.3s ease-out',
										display: 'flex',
										flexDirection: 'column',
										border: 'none',
										padding: 0,
										pointerEvents: editCategoryModalOpen ? 'none' : 'auto'
									}}
									onClick={(e) => {
										e.stopPropagation()
									}}
								>
									{/* Close button with up chevron - positioned above container, centered */}
									<div
										style={{
											position: 'absolute',
											top: '-70px',
											left: '50%',
											transform: 'translateX(-50%)',
											cursor: 'pointer',
											zIndex: 10001,
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'center',
											gap: '8px',
											transition: 'all 0.2s ease',
											opacity: modalAnimating ? 1 : 0,
											transitionDelay: modalAnimating ? '0.2s' : '0s',
										}}
										onClick={(e) => {
											e.stopPropagation();
											closeComposerModal();
										}}
										onMouseEnter={(e) => {
											e.currentTarget.style.opacity = '1';
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.opacity = modalAnimating ? '1' : '0';
										}}
									>
										<div
											style={{
												width: 'auto',
												height: '40px',
												padding: '0px 12px',
												borderRadius: '12px',
												backgroundColor: 'rgba(255, 255, 255, 0.3)',
												display: 'flex',
												gap: '5px',
												alignItems: 'center',
												justifyContent: 'center',
												boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
												transition: 'all 0.2s ease',
											}}
										>
											<TbChevronUp
												style={{
													fontSize: '22px',
													color: '#fff',
													opacity: 0.8,
													transition: 'color 0.2s ease'
												}}
											/>
														<span
											style={{
												color: 'rgba(255, 255, 255, 0.9)',
												fontSize: '14px',
												fontWeight: 500,
												textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
											}}
										>
											Close
										</span>
										</div>
							
									</div>
									{/* Modal Content */}
									<div
										style={{
											padding: '20px',
											flex: 1,
											overflowY: 'auto',
											opacity: modalAnimating ? 1 : 0,
											transform: modalAnimating ? 'translateY(0)' : 'translateY(20px)',
											transition: 'all 0.4s ease-out',
											transitionDelay: modalAnimating ? '0.1s' : '0s',
											display: 'flex',
											flexDirection: 'column',
										}}
									>
										{renderFullComposer(true)}
									</div>
								</div>
							</div>
						</Modal>
					)}
				</>
			) : (
				/* Desktop: Full inline composer */
				renderFullComposer()
			)}

			{/* Commented out - Preview display below composer - now using inline previews */}
				{/* {selectedPreviewUrl &&
					<div style={{ position: 'relative', padding: ((fileType === 'aud-rec') || (fileType === 'vid-rec')) ? windowWidth <= 600 ? '20px 0px 0px 10px' : '10px 30px 0px 82px' : '10px 30px 0px 82px' }}>
						{fileType === 'img' &&
							<>
								<img
									src={selectedPreviewUrl}
									alt={t('common.selectedImage')}
									style={{
										maxWidth: '100%',
										height: 'auto',
										borderRadius: '6px',
										overflow: 'hidden',
										margin: '0px 0px 10px 0px',
									}}
								/>
								<div style={{ position: 'absolute', top: '20px', right: '40px' }}>
									<IconButton onClick={handleRemoveContent} size="small"
										sx={{ border: '1px solid #D3DCE7', backgroundColor: '#fff', p: '5px', height: '28px', width: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
										<IoClose style={{ fontSize: '16px', color: '#222' }} />
									</IconButton>
								</div>
							</>

						}
						{fileType === 'vid' &&
							<div style={{ position: 'relative', width: '100%', borderRadius: '8px', overflow: 'hidden', marginBottom: '10px' }}>
								<div style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10 }}>
									<IconButton onClick={handleRemoveContent} size="small"
										sx={{ border: '1px solid #D3DCE7', backgroundColor: '#fff', p: '5px', height: '28px', width: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
										<IoClose style={{ fontSize: '16px', color: '#222' }} />
									</IconButton>
								</div>
								<VideoJS
									style={{
										width: '100%',
										aspectRatio: '16/9'
									}}
									options={{
										autoplay: false,
										controls: true,
										fluid: true,
										sources: [{
											src: selectedPreviewUrl,
											type: 'video/mp4'
										}]
									}}
									page={'influencer_banner'} />
							</div>

						}
						{fileType === 'vey' &&
							<div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: '8px', overflow: 'hidden', marginBottom: '10px' }}>
								<div style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10 }}>
									<IconButton onClick={handleRemoveContent} size="small"
										sx={{ border: '1px solid #D3DCE7', backgroundColor: '#fff', p: '5px', height: '28px', width: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
										<IoClose style={{ fontSize: '16px', color: '#222' }} />
									</IconButton>
								</div>
								<iframe
									style={{
										position: 'absolute',
										top: 0,
										left: 0,
										width: '100%',
										height: '100%',
										border: 'none'
									}}
									src={"https://www.youtube-nocookie.com/embed/" + embedUrlKey + "?rel=0&amp;modestbranding=1"}
									title={t('dashboard.widgets.articles.youtubePlayer')}
									frameBorder="0"
									allow="accelerometer; encrypted-media; gyroscope; fullscreen;"
									referrerPolicy="strict-origin-when-cross-origin"
									allowFullScreen
								/>
							</div>
						}
						{fileType === 'vel' &&
							<div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: '8px', overflow: 'hidden', marginBottom: '10px' }}>
								<div style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10 }}>
									<IconButton onClick={handleRemoveContent} size="small"
										sx={{ border: '1px solid #D3DCE7', backgroundColor: '#fff', p: '5px', height: '28px', width: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
										<IoClose style={{ fontSize: '16px', color: '#222' }} />
									</IconButton>
								</div>
								<iframe
									style={{
										position: 'absolute',
										top: 0,
										left: 0,
										width: '100%',
										height: '100%',
										border: 'none'
									}}
									src={"https://www.loom.com/embed/" + embedUrlKey + "?autoplay=false"}
									title="Loom video player"
									frameBorder="0"
									allowFullScreen
								/>
							</div>
						}
						{['gos', 'god'].includes(fileType) &&
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									height: '60px',
									border: '1px solid #E4EBF6',
									borderRadius: '8px',
									padding: '0 16px',
									marginBottom: '16px',
									position: 'relative'
								}}
							>
								{fileType === 'gos' && <img src={GoogleSheetsImage} alt={t('dashboard.paidDms.googleSheets')} style={{ width: '24px', marginRight: '16px' }} />}
								{fileType === 'god' && <img src={GoogleDocsImage} alt={t('dashboard.paidDms.googleDocs')} style={{ width: '24px', marginRight: '16px' }} />}
								<Box sx={{ flexGrow: 1 }}>
									<p style={{ fontWeight: 500, fontFamily: 'Roboto', color: '#000', margin: 0 }}>
										{fileType === 'gos' && 'Google Sheets'}
										{fileType === 'god' && 'Google Docs'}
									</p>
								</Box>
								<Button
									variant="contained"
									disableElevation
									onClick={() => handleFullscreenOpen(normalizeExternalHref(selectedPreviewUrl), fileType)}
									rel="noopener noreferrer"
									sx={{
										padding: '5px 7px',
										display: 'inline-block',
										marginLeft: 'auto',
										borderRadius: '8px !important',
										textTransform: 'none',
										textAlign: 'center',
										backgroundColor: 'var(--brand-color)',
										'&:hover': {
											backgroundColor: 'var(--brand-color-hover)'
										},
										cursor: 'pointer'
									}}>
									<span className="fa fa-search" style={{ fontWeight: '500', fontSize: '14px' }}>
										Open
									</span>
								</Button>
								<IconButton
									onClick={handleRemoveContent}
									sx={{
										position: 'absolute', top: -10, right: -10, zIndex: 1,
										border: '1px solid #D3DCE7', backgroundColor: '#fff',
										height: 28, width: 28, p: '5px',
										'&:hover': { backgroundColor: '#f5f5f5' },
									}}
								>
									<IoClose className={'content-upload-icons'} />
								</IconButton>
							</Box>
						}


						{fileType === 'aud' &&
							<div style={{ position: 'relative', width: '100%', marginBottom: '10px' }}>
								<div style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10 }}>
									<IconButton onClick={handleRemoveContent} size="small"
										sx={{ border: '1px solid #D3DCE7', backgroundColor: '#fff', p: '5px', height: '28px', width: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
										<IoClose style={{ fontSize: '16px', color: '#222' }} />
									</IconButton>
								</div>
								<div style={{
									width: '100%',
									padding: '12px',
									backgroundColor: '#F5F9FF',
									borderRadius: '8px',
									border: '1px solid #E4EBF6'
								}}>
									<ReactAudioPlayer
										className='react-player'
										src={selectedPreviewUrl}
										controls
										style={{ width: '100%' }}
									/>
								</div>
							</div>
						}
						{['mgm', 'mzm', 'mmt', 'mot'].includes(fileType) &&
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									height: '60px',
									border: '1px solid #E4EBF6',
									borderRadius: '8px',
									padding: '0 16px',
									marginBottom: '16px',
									position: 'relative'
								}}
							>
								{fileType === 'mot' && <TbLink style={{ width: '24px', marginRight: '16px', color: '#6C89AF' }} />}
								{fileType === 'mgm' && <img src={GoogleMeetImage} alt={t('community.composer.googleMeet')} style={{ width: '24px', marginRight: '16px' }} />}
								{fileType === 'mmt' && <img src={MicrosoftTeams} alt={t('community.composer.microsoftTeams')} style={{ width: '24px', marginRight: '16px' }} />}
								{fileType === 'mzm' && <img src={ZoomImage} alt={t('profile.zoom')} style={{ width: '24px', marginRight: '16px' }} />}
								<Box sx={{ flexGrow: 1 }}>
									<p style={{ fontWeight: 500, fontFamily: 'Roboto', color: '#000', margin: 0 }}>
										{fileType === 'mot' && 'Meeting'}
										{fileType === 'mgm' && 'Google Meet'}
										{fileType === 'mmt' && 'Microsoft Teams'}
										{fileType === 'mzm' && 'Zoom'}
									</p>
								</Box>

								<Button
									variant="contained"
									disableElevation
									href={normalizeExternalHref(selectedPreviewUrl)}
									target="_blank"
									rel="noopener noreferrer"
									sx={{
										padding: '5px 7px',
										display: 'inline-block',
										marginLeft: 'auto',
										borderRadius: '8px !important',
										textTransform: 'none',
										textAlign: 'center',
										backgroundColor: 'var(--brand-color)',
										'&:hover': {
											backgroundColor: 'var(--brand-color-hover)'
										}
									}}  
								>
									<span className="fa fa-search" style={{ fontWeight: '500', fontSize: '14px' }}>
										Join
									</span>
								</Button>

								<IconButton
									onClick={handleRemoveContent}
									sx={{
										position: 'absolute', top: -10, right: -10, zIndex: 1,
										border: '1px solid #D3DCE7', backgroundColor: '#fff',
										height: 28, width: 28, p: '5px',
										'&:hover': { backgroundColor: '#f5f5f5' },
									}}
								>
									<IoClose className={'content-upload-icons'} />
								</IconButton>
							</Box>
						}
						{fileType === 'pdf' &&
							<>
								<PDFPost pdf_src={selectedPreviewUrl} content_width_type={'post'} pre_upload={true} />


								<div style={{ position: 'absolute', zIndex: '2999', top: '40px', right: '40px' }}>
									<IconButton onClick={handleRemoveContent} size="small"
										sx={{ border: '1px solid #D3DCE7', backgroundColor: '#fff', p: '5px', height: '28px', width: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
										<IoClose style={{ fontSize: '16px', color: '#222' }} />
									</IconButton>
								</div>
							</>
						}

					</div>
				} */}

			<Modal
				open={meetingModalOpen}
				onClose={() => {
					setMeetingModalOpen(false);
					if (meetingType === 'post') {
						setFileType('txt');
						setMeetingLink('');
					} else if (meetingType === 'thread') {
						setFileTypeThread('txt');
						setMeetingLinkThread('');
					} else if (meetingType === 'comment') {
						setFileTypeComment('txt');
						setMeetingLinkComment('');
					} else if (meetingType === 'edit') {
						setFileTypeEdit('txt');
						setMeetingLinkEdit('');
						setMeetingImageEdit('');
						setMeetingImageFileNameEdit('');
					}
				}}
				aria-labelledby="Thumbnail selection"
				aria-describedby="Select thumbnail from video"
				style={{ zIndex: '9999' }}
			>
				<>
					<SnackbarAlert
						msg={snackbarMsgMeeting}
						open={snackbarOpenMeeting}
						severity={snackbarSeverityMeeting}
						backgroundColor={snackbarColorMeeting}
						handleClose={() => {
							setSnackbarOpenMeeting(false)
						}}
						autoHide={null}
					/>
					<Box sx={{
						position: 'absolute',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						bgcolor: '#fff',
						maxWidth: '600px',
						width: props.windowWidth < 350 ? '300px' : (props.windowWidth < 500 ? '90%' : '450px'),
						minWidth: '320px',
						borderRadius: '6px',
						boxShadow: 24,
						padding: '30px',
						boxSizing: 'border-box'
					}}>
						<div style={{ position: 'absolute', top: '10px', right: '10px', '&:hover': { cursor: 'pointer' } }}>
							<a onClick={() => {
								setMeetingModalOpen(false);
								if (meetingType === 'post') {
									setFileType('txt');
									setMeetingLink('');
									setPostMeetingTime('');
									setSelectedFile('');
									if (eventImagePreviewUrl) {
										URL.revokeObjectURL(eventImagePreviewUrl);
									}
									setEventImagePreviewUrl('');
									setEventImageFileName('');
								} else if (meetingType === 'thread') {
									setFileTypeThread('txt');
									setMeetingLinkThread('');
									setPostMeetingTime('');
								} else if (meetingType === 'comment') {
									setFileTypeComment('txt');
									setMeetingLinkComment('');
									setPostMeetingTime('');
								} else if (meetingType === 'edit') {
									setFileTypeEdit('txt');
									setMeetingLinkEdit('');
									setMeetingImageEdit('');
									setMeetingImageFileNameEdit('');
								}
							}}>
								<IconButton size="small"
									sx={{ border: '1px solid #D3DCE7', backgroundColor: '#fff', p: '5px', height: '28px', width: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
									<IoClose style={{ fontSize: '16px', color: '#222' }} />
								</IconButton>
							</a>
						</div>

						<div style={{ display: 'block', width: '100%', height: '100%', margin: '0px', textAlign: 'center' }}>
							{(() => {
								const currentFileType =
									meetingType === 'post' ? fileType :
										meetingType === 'thread' ? fileTypeThread :
											meetingType === 'comment' ? fileTypeComment :
												meetingType === 'edit' ? fileTypeEdit : '';

								if (currentFileType === 'mot') {
									return <TbLink style={{ verticalAlign: 'middle', color: '#6C89AF', width: '24px', height: '24px', margin: '-3px 2px 0px 8px' }} />;
								} else {
									const imageSrc =
										currentFileType === 'mzm' ? ZoomImage :
											currentFileType === 'mgm' ? GoogleMeetImage :
												currentFileType === 'mmt' ? MicrosoftTeams : null;

									return imageSrc && <img src={imageSrc} style={{ verticalAlign: 'middle', height: 'auto', height: currentFileType === 'mzm' ? '14px' : (currentFileType === 'mgm' ? '32px' : '26px'), margin: '-3px 2px 0px 8px' }} />;
								}
							})()}
						</div>

						<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
							<h3
								style={{
									color: '#000',
									fontSize: '18px',
									fontFamily: 'Roboto',
									fontWeight: '600',
									textAlign: 'center',
								}}
							>
								Add {(() => {
									const currentFileType =
										meetingType === 'post' ? fileType :
											meetingType === 'thread' ? fileTypeThread :
												meetingType === 'comment' ? fileTypeComment :
													meetingType === 'edit' ? fileTypeEdit : '';

									switch (currentFileType) {
										case 'mzm': return t('community.composer.zoomMeeting');
										case 'mgm': return t('community.composer.googleMeetMeeting');
										case 'mmt': return t('community.composer.microsoftTeamsMeeting');
										case 'mot': return t('community.composer.meeting');
										default: return '';
									}
								})()}
							</h3>
						</div>
						<p style={{ color: '#000', width: props.windowWidth < 370 ? '100%' : '100%', fontWeight: '600', fontSize: '14px', margin: '10px auto 10px 0px', textAlign: 'center', }}>{t('community.composer.addMeetingLink')}</p>

						{/* Meeting URL Input */}
						<form autoComplete="off" style={{
							backgroundColor: '#ffffff',
							color: '#fff',
							borderRadius: '8px',
							padding: '2px 7px',
							display: 'flex',
							flexDirection: 'column',
							border: '1px solid #D9DADB',
							width: '100%',
							fontFamily: 'roboto',
							minWidth: '280px',
							zIndex: '10',
							boxSizing: 'border-box',
							margin: 'auto',
							marginBottom: '10px',
						}}>
							<input
								type="text"
								className={'text-input-with-button email-field'}
								id={"emailCaptureInput"}
								placeholder={t('community.placeholders.meetingUrl')}
								maxLength={200}
								style={{
									border: 'none',
									padding: '0px 0px 0px 0px',
									width: '100%',
									textAlign: 'center',
									height: '40px',
									fontWeight: '400',
									color: '#3B55F3',
									fontSize: '16px',
									fontFamily: 'Roboto'
								}}
								value={meetingType === 'post' ? meetingLink : meetingType === 'thread' ? meetingLinkThread : meetingType === 'comment' ? meetingLinkComment : meetingLinkEdit}
								autoComplete='off'
								onChange={(event) => {
									if (meetingType === 'post') {
										setMeetingLink(event.target.value)
									} else if (meetingType === 'thread') {
										setMeetingLinkThread(event.target.value)
									} else if (meetingType === 'comment') {
										setMeetingLinkComment(event.target.value)
									} else if (meetingType === 'edit') {
										setMeetingLinkEdit(event.target.value)
									}
								}}
							/>
						</form>

						<p style={{ color: '#6D89AF', width: props.windowWidth < 370 ? '100%' : '100%', fontWeight: '600', fontSize: '12px', margin: '0px auto 10px 0px', textAlign: 'center', }}>
							<BiInfoCircle style={{ verticalAlign: 'middle', marginTop: '-2px', paddingRight: '3px', fontSize: '16px' }} />
							{meetingType === 'post' ? (
								fileType === 'mzm' ? t('community.composer.allZoomLinksAccepted') :
									fileType === 'mgm' ? t('community.composer.allGoogleMeetLinksAccepted') :
										fileType === 'mmt' ? t('community.composer.allMicrosoftTeamsLinksAccepted') :
											fileType === 'mot' ? t('community.composer.allMeetingLinksAccepted') :
												fileType === 'pdf' ? t('community.composer.allPdfFilesAccepted') :
													fileType === 'vey' ? t('community.composer.allYouTubeUrlsAccepted') :
														fileType === 'vel' ? t('community.composer.allLoomUrlsAccepted') :
															t('community.composer.allLinksAccepted')
							) : meetingType === 'thread' ? (
								fileTypeThread === 'mzm' ? t('community.composer.allZoomLinksAccepted') :
									fileTypeThread === 'mgm' ? t('community.composer.allGoogleMeetLinksAccepted') :
										fileTypeThread === 'mmt' ? t('community.composer.allMicrosoftTeamsLinksAccepted') :
											fileTypeThread === 'mot' ? t('community.composer.allMeetingLinksAccepted') :
												fileTypeThread === 'pdf' ? t('community.composer.allPdfFilesAccepted') :
													fileTypeThread === 'vey' ? t('community.composer.allYouTubeUrlsAccepted') :
														fileTypeThread === 'vel' ? t('community.composer.allLoomUrlsAccepted') :
															t('community.composer.allLinksAccepted')
							) : meetingType === 'comment' ? (
								fileTypeComment === 'mzm' ? t('community.composer.allZoomLinksAccepted') :
									fileTypeComment === 'mgm' ? t('community.composer.allGoogleMeetLinksAccepted') :
										fileTypeComment === 'mmt' ? t('community.composer.allMicrosoftTeamsLinksAccepted') :
											fileTypeComment === 'mot' ? t('community.composer.allMeetingLinksAccepted') :
												fileTypeComment === 'pdf' ? t('community.composer.allPdfFilesAccepted') :
													fileTypeComment === 'vey' ? t('community.composer.allYouTubeUrlsAccepted') :
														fileTypeComment === 'vel' ? t('community.composer.allLoomUrlsAccepted') :
															t('community.composer.allLinksAccepted')
							) : (
								fileTypeEdit === 'mzm' ? t('community.composer.allZoomLinksAccepted') :
									fileTypeEdit === 'mgm' ? t('community.composer.allGoogleMeetLinksAccepted') :
										fileTypeEdit === 'mmt' ? t('community.composer.allMicrosoftTeamsLinksAccepted') :
											fileTypeEdit === 'mot' ? t('community.composer.allMeetingLinksAccepted') :
												fileTypeEdit === 'pdf' ? t('community.composer.allPdfFilesAccepted') :
													fileTypeEdit === 'vey' ? t('community.composer.allYouTubeUrlsAccepted') :
														fileTypeEdit === 'vel' ? t('community.composer.allLoomUrlsAccepted') :
															t('community.composer.allLinksAccepted')
							)}
						</p>




						{/* Time Picker */}

						{/* {
                           meetingType !== 'edit' && (
                              <> */}
						<p style={{ color: '#000', width: props.windowWidth < 370 ? '100%' : '100%', fontWeight: '600', fontSize: '14px', margin: '30px auto 10px 0px', textAlign: 'center', }}>{t('community.composer.selectMeetingTime')}</p>
						<form autoComplete="off" style={{
							backgroundColor: '#ffffff',
							color: '#fff',
							borderRadius: '8px',
							padding: props.windowWidth > 600 ? '2px 17px 2px 7px' : '0px',
							display: 'flex',
							flexDirection: 'column',
							border: '1px solid #D9DADB',
							width: '100%',
							fontFamily: 'roboto',
							minWidth: '280px',
							zIndex: '10',
							boxSizing: 'border-box',
							margin: props.windowWidth > 600 ? '0px auto auto auto' : '0px auto',
							position: 'relative',
						}}>
							<div style={{ position: 'relative', width: '100%', display: 'flex' }}>
								<input
									type="datetime-local"
									className={'text-input-with-button time-field'}
									id={"meetingTimePicker"}
									style={{
										border: 'none',
										padding: '0px 0px',
										width: props.windowWidth > 600 ? '100%' : '90%',
										margin: 'auto',
										backgroundColor: 'white',
										textAlign: 'center',
										height: '40px',
										fontWeight: '400',
										color: '#3B55F3',
										fontSize: '16px',
										fontFamily: 'Roboto'
									}}
									value={postMeetingTime ? (() => {
										// Convert UTC time to local time for display
										const utcDate = new Date(postMeetingTime);
										if (isNaN(utcDate.getTime())) return '';
										// Get local time string in format YYYY-MM-DDTHH:mm
										const year = utcDate.getFullYear();
										const month = String(utcDate.getMonth() + 1).padStart(2, '0');
										const day = String(utcDate.getDate()).padStart(2, '0');
										const hours = String(utcDate.getHours()).padStart(2, '0');
										const minutes = String(utcDate.getMinutes()).padStart(2, '0');
										return `${year}-${month}-${day}T${hours}:${minutes}`;
									})() : ''}
									min={(() => {
										// Set min to current local time
										const now = new Date();
										const year = now.getFullYear();
										const month = String(now.getMonth() + 1).padStart(2, '0');
										const day = String(now.getDate()).padStart(2, '0');
										const hours = String(now.getHours()).padStart(2, '0');
										const minutes = String(now.getMinutes()).padStart(2, '0');
										return `${year}-${month}-${day}T${hours}:${minutes}`;
									})()}
									onChange={(event) => {
										// Convert local time to UTC for storage
										const localDate = new Date(event.target.value);
										if (isNaN(localDate.getTime())) {
											setPostMeetingTime('');
											return;
										}
										// Store as UTC ISO string
										setPostMeetingTime(localDate.toISOString());
									}}
								/>

								{/* {
									props.windowWidth > 600 &&
									<span style={{
										position: 'absolute',
										right: '50px',
										top: '50%',
										transform: 'translateY(-50%)',
										color: '#3B55F3',
										fontSize: '13px',
										pointerEvents: 'none'
									}}>
										{Intl.DateTimeFormat().resolvedOptions().timeZone}
									</span>
								} */}
							</div>

							{
								props.windowWidth > 600 &&


								<div
									style={{
										position: 'absolute',
										right: '15px',
										top: '50%',
										transform: 'translateY(-50%)',
										cursor: 'pointer'
									}}
									onClick={() => document.getElementById('meetingTimePicker').showPicker()}
								>
									<div style={{ backgroundColor: '#3B55F3', color: 'white', padding: '2px', display: 'flex', borderRadius: '50%', fontSize: '12px', fontWeight: '600', width: '20px', height: '20px', textAlign: 'center' }}>
										<TbCalendar style={{ verticalAlign: 'middle', margin: 'auto' }} />
									</div>
								</div>
							}

						</form>
						<p style={{ color: '#6D89AF', width: props.windowWidth < 370 ? '100%' : '100%', fontWeight: '600', fontSize: '12px', margin: '10px auto 10px 0px', textAlign: 'center', }}>
							{t('community.composer.timeTimezone')}
						</p>

						{
							postMeetingTime !== '' ? (
								<p
									// variant="outlined"
									style={{
										cursor: 'pointer',
										textAlign: 'center',
										color: 'var(--danger-color)',
										fontSize: '14px',
										textTransform: 'none',
										width: 'auto',
										fontWeight: '600',
										maxWidth: '400px',
										marginTop: '5px',
										bottom: '20px',
										':hover': {
											bgcolor: 'var(--brand-color-hover)',
											color: 'white',
											borderColor: 'var(--brand-color-hover)',
										},
									}}
									onClick={() => setPostMeetingTime('')}
								>
									Clear Time
								</p>
							)
								:
								<p

									// variant="outlined"
									style={{
										// cursor: 'pointer',
										width: '100%',
										textAlign: 'center',
										color: '#6D89AF',
										fontSize: '14px',
										textTransform: 'none',
										width: props.windowWidth < 370 ? '100%' : '100%',
										fontWeight: '600',
										maxWidth: '400px',
										marginTop: '5px',
										bottom: '20px',
										// ':hover': {
										//    bgcolor: 'var(--brand-color-hover)',
										//    color: 'white',
										//    borderColor: 'var(--brand-color-hover)',
										// },
									}}
								// onClick={() => setPostMeetingTime('')}
								>
									No Meeting Time Selected
								</p>

						}

						{/* Optional event image (post only) */}
						{(meetingType === 'post' || meetingType === 'edit') && (
							<div style={{ marginTop: '18px', width: '100%' }}>
								<p style={{ color: '#000', width: '100%', fontWeight: '600', fontSize: '14px', margin: '0px auto 10px auto', textAlign: 'center' }}>
									{t('community.composer.addEventImage')}
								</p>
								<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
									<Button
										variant="outlined"
										component="label"
										sx={{
											textTransform: 'none',
											borderRadius: '8px',
											borderColor: '#D9DADB',
											color: '#1D1D1D',
											fontWeight: '600',
											maxWidth: '400px',
											width: '100%',
										}}
									>
										{(eventImageFileName || meetingImageFileNameEdit) ? t('community.composer.changeImage', { filename: eventImageFileName || meetingImageFileNameEdit }) : t('community.composer.chooseImage')}
										<input
											hidden
											type="file"
											accept="image/png,image/jpeg"
											onChange={(e) => {
												const f = e.target.files?.[0];
												if (!f) return;
												if (!f.type?.startsWith('image/')) return;
												if (eventImagePreviewUrl || meetingImageEdit) {
													URL.revokeObjectURL(eventImagePreviewUrl || meetingImageEdit);
												}
												const url = URL.createObjectURL(f);
												if (meetingType === 'edit') {
													setMeetingImageEdit(url);
													setMeetingImageFileNameEdit(f.name);
													setSelectedFileEdit(f);
												} else {													
													setEventImagePreviewUrl(url);
													setEventImageFileName(f.name);
													setSelectedFile(f);
												}
											}}
										/>
									</Button>

									{(eventImagePreviewUrl || meetingImageEdit) && (
										<div style={{ width: '100%', maxWidth: '400px' }}>
											<img
												src={eventImagePreviewUrl || (meetingImageEdit.startsWith('blob:') || meetingImageEdit.startsWith('http') ? meetingImageEdit : paths.CONTENT_CLOUDFRONT + meetingImageEdit)}
												alt={t('community.post.event')}
												style={{
													width: '100%',
													maxHeight: '220px',
													objectFit: 'cover',
													borderRadius: '10px',
													border: '1px solid #E4EBF6',
												}}
											/>
											<Button
												variant="text"
												onClick={() => {
													if (meetingType === 'edit') {
														if (meetingImageEdit) {
															URL.revokeObjectURL(meetingImageEdit);
														}
														setMeetingImageEdit('');
														setMeetingImageFileNameEdit('');
														setSelectedFileEdit('');
													} else {
														if (eventImagePreviewUrl) {
															URL.revokeObjectURL(eventImagePreviewUrl);
														}
														setEventImagePreviewUrl('');
														setEventImageFileName('');
														setSelectedFile('');
													}
												}}
												sx={{
													textTransform: 'none',
													color: 'var(--danger-color)',
													fontWeight: '600',
													marginTop: '6px',
												}}
											>
												Remove image
											</Button>
										</div>
									)}
								</div>
							</div>
						)}

						{/* </>
                                    )
                                 }       */}





						<Button
							fullWidth
							variant="contained"
							sx={{
								backgroundColor: 'var(--brand-color)', fontSize: '14px',
								textTransform: 'none', width: props.windowWidth < 370 ? '100%' : '100%', borderRadius: '6px',
								boxShadow: 'none', fontWeight: '600',
								maxWidth: '400px',
								marginTop: '10px',
								':hover': {
									bgcolor: 'var(--brand-color-hover)',
									color: 'white',
									boxShadow: 'none',
								},
							}}
							className="image_update_button"
							type="submit" value="submit"
							disabled={meetingType === 'post' ? meetingLink === '' : meetingType === 'thread' ? meetingLinkThread === '' : meetingType === 'comment' ? meetingLinkComment === '' : meetingLinkEdit === ''}
							onClick={() => {
								checkMeetingLink();
							}}
						>{t('community.composer.addMeeting')}</Button>

					</Box>
				</>
			</Modal>

			{/* Doc Modal */}
			<Modal
				open={docModalOpen}
				onClose={() => {
					setDocModalOpen(false);
					setActiveDocFileType('');
					if (docType === 'post') {
						setFileType('txt');
						setDocLink('');
					} else if (docType === 'thread') {
						setFileTypeThread('txt');
						setDocLinkThread('');
					} else if (docType === 'comment') {
						setFileTypeComment('txt');
						setDocLinkComment('');
					} else if (docType === 'edit') {
						setFileTypeEdit('txt');
						setDocLinkEdit('');
					}
				}}
				aria-labelledby="Thumbnail selection"
				aria-describedby="Select thumbnail from video"
				style={{ zIndex: '9999' }}
			>
				<>
					<SnackbarAlert
						msg={snackbarMsgDoc}
						open={snackbarOpenDoc}
						severity={'error'}
						backgroundColor={'var(--danger-color)'}
						handleClose={() => {
							setSnackbarOpenDoc(false)
						}}
						autoHide={null}
					/>
					<Box sx={{
						position: 'absolute',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						bgcolor: '#fff',
						maxWidth: '600px',
						width: props.windowWidth < 350 ? '300px' : (props.windowWidth < 500 ? '90%' : '450px'),
						minWidth: '320px',
						borderRadius: '6px',
						boxShadow: 24,
						padding: '30px',
						boxSizing: 'border-box'
					}}>
						<div style={{ position: 'absolute', top: '10px', right: '10px', '&:hover': { cursor: 'pointer' } }}>
							<a onClick={() => {
								setDocModalOpen(false);
								setActiveDocFileType('');
								if (docType === 'post') {
									setFileType('txt');
									setDocLink('');
								} else if (docType === 'thread') {
									setFileTypeThread('txt');
									setDocLinkThread('');
								} else if (docType === 'comment') {
									setFileTypeComment('txt');
									setDocLinkComment('');
								} else if (docType === 'edit') {
									setFileTypeEdit('txt');
									setDocLinkEdit('');
								}
							}}>
								<IconButton size="small"
									sx={{ border: '1px solid #D3DCE7', backgroundColor: '#fff', p: '5px', height: '28px', width: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
									<IoClose style={{ fontSize: '16px', color: '#222' }} />
								</IconButton>
							</a>
						</div>

						<div style={{ display: 'block', width: '100%', height: '100%', margin: '0px', textAlign: 'center' }}>
							{(() => {
								const currentFileType =
									docType === 'post' ? fileType : activeDocFileType;

								if (currentFileType === 'god') {
									return <img src={GoogleDocsImage} style={{ verticalAlign: 'middle', height: '26px', margin: '-3px 2px 0px 8px' }} />;
								} else if (currentFileType === 'gos') {
									return <img src={GoogleSheetsImage} style={{ verticalAlign: 'middle', height: '26px', margin: '-3px 2px 0px 8px' }} />;
								}
							})()}
						</div>

						<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
							<h3
								style={{
									color: 'var(--light-theme-text-color)',
									fontSize: '20px',
									fontFamily: 'Roboto',
									fontWeight: '600',
									textAlign: 'center',
								}}
							>
								{t('community.composer.add')} {(() => {
									const ft = docType === 'post' ? fileType : activeDocFileType;
									return ft === 'gos' ? t('community.composer.googleSheet') :
										ft === 'god' ? t('community.composer.googleDoc') : '';
								})()}
							</h3>

						</div>


						<form autocomplete="off" style={{
							backgroundColor: '#ffffff',
							color: '#fff',
							borderRadius: '8px',
							padding: '2px 7px',
							display: 'flex',
							border: '1px solid #D9DADB',
							width: '100%',
							fontFamily: 'roboto',
							// width: props.windowWidth < 370 ? '90%' : '100%',
							minWidth: '280px',
							zIndex: '10',
							boxSizing: 'border-box',
							margin: 'auto',
						}}>

							<input
								type="text"
								className={'text-input-with-button email-field'}
								id={"emailCaptureInput"}
								placeholder={(() => {
									const ft = docType === 'post' ? fileType : activeDocFileType;
									return ft === 'gos' ? t('community.composer.typeOrPasteSheetUrl') :
										ft === 'god' ? t('community.composer.typeOrPasteDocUrl') : '';
								})()}
								maxLength={200}
								multiline
								style={{
									border: 'none',
									padding: '0px 0px 0px 0px',
									width: '100%',
									textAlign: 'center',
									height: '40px',
									fontWeight: '400',
									color: '#3B55F3',
									fontSize: '16px',
									fontFamily: 'Roboto'
								}}
								value={docType === 'post' ? docLink : docType === 'thread' ? docLinkThread : docType === 'comment' ? docLinkComment : docLinkEdit}
								autoComplete='off'
								onChange={(event) => {
									if (docType === 'post') {
										setDocLink(event.target.value)
									} else if (docType === 'thread') {
										setDocLinkThread(event.target.value)
									} else if (docType === 'comment') {
										setDocLinkComment(event.target.value)
									} else if (docType === 'edit') {
										setDocLinkEdit(event.target.value)
									}
								}
								}
							/>


						</form>


						<p style={{ color: '#6D89AF', width: props.windowWidth < 370 ? '100%' : '100%', fontWeight: '600', fontSize: '12px', margin: '12px auto 10px 0px', textAlign: 'center', }}>
							<BiInfoCircle style={{ verticalAlign: 'middle', marginTop: '-2px', paddingRight: '3px', fontSize: '16px' }} />
							{(() => {
								const ft = docType === 'post' ? fileType : activeDocFileType;
								return ft === 'gos' ? t('community.composer.allGoogleSheetLinksAccepted') :
									ft === 'god' ? t('community.composer.allGoogleDocLinksAccepted') :
										t('community.composer.allGoogleDocOrSheetLinksAccepted');
							})()}
						</p>




						<Button
							fullWidth
							variant="contained"
							sx={{
								backgroundColor: 'var(--brand-color)', fontSize: '14px',
								textTransform: 'none', width: props.windowWidth < 370 ? '100%' : '100%', borderRadius: '6px',
								boxShadow: 'none', fontWeight: '600',
								maxWidth: '400px',
								marginTop: '10px',
								':hover': {
									bgcolor: 'var(--brand-color-hover)',
									color: 'white',
									boxShadow: 'none',
								},
							}}
							className="image_update_button"
							type="submit" value="submit"
							disabled={docType === 'post' ? docLink === '' : docType === 'thread' ? docLinkThread === '' : docType === 'comment' ? docLinkComment === '' : docLinkEdit === ''}
							onClick={() => {
								checkDocLink();
							}}
						>{docType === 'post' ? (
							fileType === 'gos' ? t('community.composer.addSheet') :
								fileType === 'god' ? t('community.composer.addDoc') : ''
						) : docType === 'thread' ? (
							fileTypeThread === 'gos' ? t('community.composer.addSheet') :
								fileTypeThread === 'god' ? t('community.composer.addDoc') : ''
						) : docType === 'comment' ? (
							fileTypeComment === 'gos' ? t('community.composer.addSheet') :
								fileTypeComment === 'god' ? t('community.composer.addDoc') : ''
						) : (
							fileTypeEdit === 'gos' ? t('community.composer.addSheet') :
								fileTypeEdit === 'god' ? t('community.composer.addDoc') : ''
						)}</Button>
					</Box>
				</>
			</Modal>

			{/* Embed URL Modal */}
			<Modal
				open={embedUrlIsOpen}
				onClose={() => {
					setEmbedUrlIsOpen(false);
					if (embedUrlType === 'post') {
						setEmbedVideoUrl('');
					} else if (embedUrlType === 'thread') {
						setEmbedVideoUrlThread('');
					} else if (embedUrlType === 'comment') {
						setEmbedVideoUrlComment('');
					} else if (embedUrlType === 'edit') {
						setEmbedVideoUrlEdit('');
					}
				}}
				aria-labelledby="Thumbnail selection"
				aria-describedby="Select thumbnail from video"
				style={{
					zIndex: 9999
				}}
			>
				<>
					<SnackbarAlert
						msg={snackbarMsgEmbed}
						open={snackbarEmbed}
						severity={'error'}
						backgroundColor={'var(--danger-color'}
						handleClose={() => {
							setSnackbarEmbed(false)
						}}
						autoHide={null}
					/>
					<Box sx={{
						position: 'absolute',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						bgcolor: '#fff',
						maxWidth: '600px',
						width: props.windowWidth < 350 ? '300px' : (props.windowWidth < 500 ? '90%' : '450px'),
						minWidth: '320px',
						borderRadius: '6px',
						boxShadow: 24,
						padding: '20px 20px',
						boxSizing: 'border-box',
						zIndex: 9999,
					}}>


						<div style={{ position: 'absolute', top: '10px', right: '10px', '&:hover': { cursor: 'pointer' } }}>
							<a onClick={() => {
								setEmbedUrlIsOpen(false);
								if (embedUrlType === 'post') {
									setEmbedVideoUrl('');
								} else if (embedUrlType === 'thread') {
									setEmbedVideoUrlThread('');
								} else if (embedUrlType === 'comment') {
									setEmbedVideoUrlComment('');
								} else if (embedUrlType === 'edit') {
									setEmbedVideoUrlEdit('');
								}
							}}>
								<IconButton size="small"
									sx={{ border: '1px solid #D3DCE7', backgroundColor: '#fff', p: '5px', height: '28px', width: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { backgroundColor: '#f5f5f5' } }}>
									<IoClose style={{ fontSize: '16px', color: '#222' }} />
								</IconButton>
							</a>
						</div>

						<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
							<h3
								style={{
									color: 'var(--light-theme-text-color)',
									fontSize: '20px',
									fontFamily: 'Roboto',
									fontWeight: '600',
									textAlign: 'center',
								}}
							>
								{t('community.composer.embedVideoUrl')}
							</h3>
						</div>


						<form autocomplete="off" style={{
							backgroundColor: '#ffffff',
							color: '#fff',
							borderRadius: '8px',
							padding: '2px 7px',
							display: 'flex',
							border: '1px solid #D9DADB',
							width: '100%',
							fontFamily: 'roboto',
							// width: props.windowWidth < 370 ? '90%' : '100%',
							minWidth: '280px',
							zIndex: '10',
							boxSizing: 'border-box',
							margin: 'auto',
						}}>

							<input
								type="text"
								className={'text-input-with-button email-field'}
								id={"emailCaptureInput"}
								placeholder={t('community.composer.typeOrPasteVideoUrl')}
								maxLength={200}
								multiline
								style={{
									border: 'none',
									padding: '0px 0px 0px 0px',
									width: '100%',
									textAlign: 'center',
									height: '40px',
									fontWeight: '400',
									color: '#3B55F3',
									fontSize: '16px',
									fontFamily: 'Roboto'
								}}
								value={embedUrlType === 'post' ? embedVideoUrl : embedUrlType === 'thread' ? embedVideoUrlThread : embedUrlType === 'comment' ? embedVideoUrlComment : embedVideoUrlEdit}
								autoComplete='off'
								onChange={(event) => {
									if (embedUrlType === 'post') {
										setEmbedVideoUrl(event.target.value)
									} else if (embedUrlType === 'thread') {
										setEmbedVideoUrlThread(event.target.value)
									} else if (embedUrlType === 'comment') {
										setEmbedVideoUrlComment(event.target.value)
									} else {
										setEmbedVideoUrlEdit(event.target.value)
									}
								}}
							/>


						</form>



						<p style={{ color: '#6D89AF', width: props.windowWidth < 370 ? '100%' : '100%', fontWeight: '600', fontSize: '12px', magrin: '0px auto 0px 0px', textAlign: 'center', }}>
							<BiInfoCircle style={{ verticalAlign: 'middle', marginTop: '-2px', paddingRight: '3px', fontSize: '16px' }} />
							{t('community.composer.youtubeOrLoomAccepted')}
						</p>

						<Button
							fullWidth
							variant="contained"
							sx={{
								backgroundColor: 'var(--brand-color)', fontSize: '14px',
								textTransform: 'none', width: props.windowWidth < 370 ? '100%' : '100%', borderRadius: '6px',
								boxShadow: 'none', fontWeight: '600',
								maxWidth: '400px',
								marginTop: '10px',
								':hover': {
									bgcolor: 'var(--brand-color-hover)',
									color: 'white',
									boxShadow: 'none',
								},
							}}
							className="image_update_button"
							type="submit" value="submit"
							onClick={() => {
								processEmbedUrlSet();
							}}
						>{t('community.composer.embed')}</Button>
					</Box>
				</>
			</Modal>

			{/* Create Category Modal */}
			{categoryModalOpen && createPortal(
				<>
					<SnackbarAlert
						msg={snackbarMsgCategory}
						open={snackbarCategory}
						severity={'error'}
						backgroundColor={'var(--danger-color)'}
						handleClose={() => {
							setSnackbarCategory(false)
						}}
						autoHide={null}
					/>
					<div
						style={{
							position: 'fixed',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							backgroundColor: 'rgba(0, 0, 0, 0.6)',
							display: 'flex',
							alignItems: windowWidth < 900 ? 'flex-end' : 'center',
							justifyContent: 'center',
							zIndex: 10000,
							padding: windowWidth < 900 ? '0px' : '20px',
							opacity: categoryModalAnimating ? 1 : 0,
							transition: 'opacity 0.3s ease-in-out',
							pointerEvents: 'auto'
						}}
						onClick={() => {
							setCategoryModalAnimating(false);
							setTimeout(() => {
								setCategoryModalOpen(false);
								setNewCategoryName('');
							}, 300);
						}}
					>
						<div
							style={{
								maxWidth: windowWidth < 600 ? '100%' : '500px',
								width: '100%',
								height: 'auto',
								maxHeight: '85vh',
								backgroundColor: '#fff',
								borderRadius: windowWidth < 900 ? '20px 20px 0px 0px' : '16px',
								position: 'relative',
								boxShadow: windowWidth < 900 ? '0 -10px 40px rgba(0, 0, 0, 0.15)' : '0 20px 60px rgba(0, 0, 0, 0.15)',
								overflow: 'hidden',
								display: 'flex',
								flexDirection: 'column',
								transform: categoryModalAnimating 
									? 'translateY(0)' 
									: windowWidth < 900 
										? 'translateY(100%)' 
										: 'scale(0.8) translateY(50px)',
								opacity: categoryModalAnimating ? 1 : 0,
								transition: 'all 0.3s ease-out',
								outline: 'none',
								pointerEvents: 'auto',
								zIndex: 10001
							}}
							onClick={(e) => e.stopPropagation()}
						>
							{/* Close button */}
							<div
								style={{
									position: 'absolute',
									top: '15px',
									right: '15px',
									cursor: 'pointer',
									zIndex: 10002,
									width: '30px',
									height: '30px',
									borderRadius: '50%',
									backgroundColor: 'rgba(0, 0, 0, 0.1)',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									transition: 'all 0.2s ease',
									opacity: categoryModalAnimating ? 1 : 0,
									transform: categoryModalAnimating ? 'scale(1)' : 'scale(0.8)',
									transitionDelay: categoryModalAnimating ? '0.2s' : '0s',
									pointerEvents: 'auto'
								}}
								onClick={() => {
									setCategoryModalAnimating(false);
									setTimeout(() => {
										setCategoryModalOpen(false);
										setNewCategoryName('');
									}, 300);
								}}
							>
								<IoClose
									style={{
										fontSize: '18px',
										color: '#666',
										transition: 'color 0.2s ease'
									}}
								/>
							</div>

							{/* Header section */}
							<div
								style={{
									padding: '25px 25px 20px 25px',
									background: 'linear-gradient(135deg, rgba(247,249,252,0.8) 0%, rgba(255,255,255,0.4) 100%)',
									borderBottom: '1px solid #E4EBF6',
									opacity: categoryModalAnimating ? 1 : 0,
									transform: categoryModalAnimating ? 'translateY(0)' : 'translateY(20px)',
									transition: 'all 0.4s ease-out',
									transitionDelay: categoryModalAnimating ? '0.1s' : '0s'
								}}
							>
								<Typography
									variant="h5"
									style={{
										color: '#1D1D1D',
										fontWeight: '700',
										fontSize: windowWidth < 600 ? '18px' : '20px',
										textAlign: 'center',
										fontFamily: 'Roboto, sans-serif',
										letterSpacing: '-0.5px',
										margin: 0
									}}
								>
									{t('community.composer.createNewCategory')}
								</Typography>
							</div>

							{/* Content section */}
							<div
								style={{
									flex: 1,
									minHeight: 0,
									overflowY: 'auto',
									padding: '20px 25px 20px 25px',
									paddingRight: '25px',
									opacity: categoryModalAnimating ? 1 : 0,
									transform: categoryModalAnimating ? 'translateY(0)' : 'translateY(20px)',
									transition: 'all 0.4s ease-out',
									transitionDelay: categoryModalAnimating ? '0.2s' : '0s',
									maxHeight: windowWidth < 900 ? 'calc(80vh - 90px - 80px)' : 'calc(80vh - 90px - 80px)',
								}}
							>
								<div style={{ marginBottom: '20px' }}>
									<Typography style={{
										color: '#333',
										fontSize: '14px',
										fontWeight: '600',
										marginBottom: '8px',
										fontFamily: 'Roboto, sans-serif',
										textAlign: 'left'
									}}>
										{t('community.composer.categoryName')}
									</Typography>
									<input 
										type="text" 
										className={'text-input'} 
										style={{
											display: 'block',
											fontWeight: '500',
											fontSize: '15px',
											padding: '12px 15px',
											color: '#1D1D1D',
											borderRadius: '8px',
											border: '1px solid #C7D1DE',
											width: '100%',
											fontFamily: 'Roboto',
											outline: 'none',
											boxSizing: 'border-box',
											pointerEvents: 'auto',
											position: 'relative',
											zIndex: windowWidth < 900 ? 10003 : 'auto'
										}} 
										placeholder={t('community.composer.enterCategoryName')} 
										maxLength={50}
										value={newCategoryName} 
										onChange={(event) => {
											setNewCategoryName(event.target.value);
										}}
										onClick={(e) => e.stopPropagation()}
										onFocus={(e) => e.stopPropagation()}
									/>
								</div>

								<Typography style={{
									color: '#6D89AF',
									fontSize: '12px',
									fontWeight: '600',
									marginTop: '10px',
									fontFamily: 'Roboto, sans-serif',
									textAlign: 'left',
									display: 'flex',
									alignItems: 'center',
									gap: '4px'
								}}>
									<BiInfoCircle style={{ fontSize: '16px' }} />
									{t('community.composer.categoryNamesHint')}
								</Typography>
							</div>

							{/* Action button section */}
							<div
								style={{
									padding: '20px 25px 25px 25px',
									borderTop: '1px solid #E4EBF6',
									flexShrink: 0,
									backgroundColor: '#fff',
									opacity: categoryModalAnimating ? 1 : 0,
									transform: categoryModalAnimating ? 'translateY(0)' : 'translateY(20px)',
									transition: 'all 0.4s ease-out',
									transitionDelay: categoryModalAnimating ? '0.3s' : '0s',
									position: windowWidth < 900 ? 'sticky' : 'relative',
									bottom: windowWidth < 900 ? '0' : 'auto',
									left: 0,
									right: 0,
									zIndex: 2,
								}}
							>
								<Button 
									disableRipple 
									fullWidth 
									variant="contained" 
									disabled={!newCategoryName || newCategoryName.trim() === ''}
									sx={{
										backgroundColor: (newCategoryName && newCategoryName.trim() !== '') ? 'var(--brand-color)' : '#E4EBF6',
										color: (newCategoryName && newCategoryName.trim() !== '') ? 'white' : '#9CA3B4',
										fontSize: '14px',
										textTransform: 'none',
										borderRadius: '12px',
										boxShadow: 'none',
										fontWeight: '600',
										padding: '12px 20px',
										border: (newCategoryName && newCategoryName.trim() !== '') ? '2px solid var(--brand-color)' : '2px solid #E4EBF6',
										transition: 'all 0.2s ease-in-out',
										cursor: (newCategoryName && newCategoryName.trim() !== '') ? 'pointer' : 'not-allowed',
										'&:hover': (newCategoryName && newCategoryName.trim() !== '') ? {
											backgroundColor: 'var(--brand-color)',
											border: '2px solid var(--brand-color)',
											opacity: 0.8,
											transform: 'translateY(-1px)',
											boxShadow: 'none',
										} : {
											backgroundColor: '#E4EBF6',
											border: '2px solid #E4EBF6',
											transform: 'none',
											boxShadow: 'none',
										},
									}} 
									type="submit" 
									onClick={async () => {
										const trimmedName = newCategoryName.trim();
										if (!trimmedName) {
											setSnackbarMsgCategory(t('community.composer.pleaseEnterValidCategoryName'));
											setSnackbarCategory(true);
											return;
										}
										
										// Check if category already exists or is pending
										const existingTags = currentTabInfo?.tags || [];
										const isPending = pendingCategories && pendingCategories.length > 0 && pendingCategories[0] === trimmedName;
										if (existingTags.includes(trimmedName) || isPending) {
											setSnackbarMsgCategory(t('community.composer.categoryAlreadyExists'));
											setSnackbarCategory(true);
											return;
										}
										
										// Create category via API immediately so it shows in the list
										try {
											await addCommunityCategory(trimmedName);
										} catch (err) {
											// Category may already exist, continue anyway
										}

										// Select the new category for this post
										setPendingCategories([trimmedName]);
										setUploadPostTags([trimmedName]);
										setCategoryModalAnimating(false);
										setTimeout(() => {
											setCategoryModalOpen(false);
											setNewCategoryName('');
											setSnackbarCategory(false);
										}, 300);
									}}
								>
									{t('community.composer.createCategory')}
								</Button>
							</div>
						</div>
					</div>
				</>,
				document.body
			)}
		</>
	);
}

export default CommunityComposer;


